import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ALL_STANDARDS } from '../types/standards';
import type { AssessmentResult, BlendedParameters, DischargeLimits, SavedSample } from '../types/calculator';
import type {
  MultiSampleReport,
  ParameterTrend,
  ProcessTrainRecommendation,
  DosingRecommendation,
} from './reportAnalysis';

// ─── Colour palette (navy / gold / white) ────────────────────────────────────
const C = {
  navy:   [10, 22, 40]   as [number, number, number],
  navyMid:[30, 58, 95]   as [number, number, number],
  gold:   [205, 164, 46] as [number, number, number],
  white:  [255, 255, 255] as [number, number, number],
  black:  [15, 23, 42]   as [number, number, number],
  grey:   [100, 116, 139] as [number, number, number],
  greyLt: [226, 232, 240] as [number, number, number],
  greyBg: [248, 250, 252] as [number, number, number],
  emerald:[5, 150, 105]  as [number, number, number],
  red:    [220, 38, 38]  as [number, number, number],
  amber:  [217, 119, 6]  as [number, number, number],
  emeraldBg: [236, 253, 245] as [number, number, number], // emerald-50 — PASS background
  redBg:     [254, 242, 242] as [number, number, number], // red-50 — FAIL background
  goldBg:    [255, 251, 235] as [number, number, number], // amber-50 — warning background
};

// ─── Shared helpers ───────────────────────────────────────────────────────────

/** Format any date value to "dd Month yyyy" regulatory format */
function fmtDate(d: Date | string | null | undefined): string {
  if (!d) return '—';
  try {
    const dt = d instanceof Date ? d : new Date(d as string);
    if (!isNaN(dt.getTime())) {
      return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    }
  } catch { /* fall through */ }
  return String(d);
}

function drawHr(doc: jsPDF, y: number, margin: number, pageW: number, color = C.greyLt) {
  doc.setDrawColor(...color);
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageW - margin, y);
}

/** Draw the company header block — call once per page if needed */
function drawPageHeader(
  doc: jsPDF,
  pageW: number,
  margin: number,
  stdText: string,
  reportRef: string,
): number {
  let y = 14;
  const rightColW = 88; // max width for right-side text block
  doc.setFillColor(...C.navy);
  doc.rect(0, 0, pageW, 22, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...C.white);
  doc.text('REYNOLDS & BAUHM LTD', margin, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(200, 210, 225);
  doc.text('Wastewater Assessment & Treatment Solutions', margin, y + 5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...C.gold);
  // Truncate long standard text to prevent overflow
  const shortStd = stdText.length > 40 ? stdText.slice(0, 38) + '…' : stdText;
  doc.text(shortStd, pageW - margin, y, { align: 'right', maxWidth: rightColW });
  // Reference number — shrink font if long to guarantee it fits on one line
  const refFontSize = reportRef.length > 30 ? 7.5 : reportRef.length > 22 ? 8 : 9;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(refFontSize);
  doc.setTextColor(...C.gold);
  doc.text(reportRef, pageW - margin, y + 5.5, { align: 'right', maxWidth: 130 });

  return 28; // y below header
}

/** Draw the standard footer on every page */
function drawFooters(
  doc: jsPDF,
  pageCount: number,
  pageW: number,
  pageH: number,
  margin: number,
  reportRef: string,
  stdName: string,
  disclaimer: string,
) {
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const footerY = pageH - 14;
    doc.setFillColor(...C.navy);
    doc.rect(0, footerY - 2, pageW, 16, 'F');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(200, 210, 225);
    doc.text(`Reynolds & Bauhm Ltd  |  ${reportRef}  |  ${stdName}`, margin, footerY + 4);

    doc.setFontSize(6.5);
    doc.setTextColor(160, 175, 195);
    doc.text(disclaimer, pageW / 2, footerY + 8, { align: 'center', maxWidth: pageW - margin * 2 });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...C.gold);
    doc.text(`Page ${i} / ${pageCount}`, pageW - margin, footerY + 4, { align: 'right' });
  }
}

// ─── Regulatory watermark (faint diagonal stamp on each page) ────────────────
// Uses the actual regulatory body name and accreditation mark from ALL_STANDARDS
function drawWatermark(doc: jsPDF, pageW: number, pageH: number, stdCode?: string) {
  const std = ALL_STANDARDS.find(s => s.code === stdCode);
  // Primary line: regulatory body name (shortened if too long for watermark)
  const bodyLine = std
    ? (stdCode === 'UK_EA'     ? 'ENVIRONMENT AGENCY'
     : stdCode === 'AU_EPA'    ? 'AUSTRALIAN EPA'
     : stdCode === 'SA_WATER'  ? 'SA WATER — TRADE WASTE'
     : stdCode === 'EU_UWWTD'  ? 'EU URBAN WASTEWATER DIRECTIVE'
     : stdCode === 'US_EPA'    ? 'US EPA / NPDES'
     : std.regulatoryBody.toUpperCase())
    : 'REGULATORY ASSESSMENT';
  // Secondary line: certification mark + permit type
  const certLine = std
    ? `${std.report.certificationMark} · ${std.permitType}`.toUpperCase()
    : 'REYNOLDS & BAUHM LTD';
  try {
    doc.saveGraphicsState();
    const gs = (doc as any).GState({ opacity: 0.045, 'fill-opacity': 0.045 });
    doc.setGState(gs);
    doc.setTextColor(10, 22, 40);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(28);
    doc.text(bodyLine, pageW / 2, pageH / 2 - 10, { angle: 45, align: 'center' });
    doc.setFontSize(14);
    doc.text(certLine, pageW / 2, pageH / 2 + 8, { angle: 45, align: 'center' });
    doc.restoreGraphicsState();
  } catch {
    // Fallback: use very light grey so watermark is always visible
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(28);
    doc.setTextColor(220, 225, 235);
    doc.text(bodyLine, pageW / 2, pageH / 2 - 10, { angle: 45, align: 'center' });
    doc.setFontSize(14);
    doc.text(certLine, pageW / 2, pageH / 2 + 8, { angle: 45, align: 'center' });
    doc.setTextColor(0, 0, 0);
  }
}

/** Add watermarks on every page that was produced so far */
function stampAllPages(doc: jsPDF, pageW: number, pageH: number, stdCode?: string) {
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    drawWatermark(doc, pageW, pageH, stdCode);
  }
  doc.setPage(total); // restore to last page
}

// ─── Regulatory report number builder ─────────────────────────────────────────
/** Build a country/standard-specific regulatory report number */
export function buildRegulatoryReportNumber(
  type: 'DAF' | 'SMP' | 'WQ' | 'ASM' | 'TRN' | 'INF' | 'EFF',
  standardCode?: string,
  customSuffix?: string,
): string {
  const now   = new Date();
  const yy    = String(now.getFullYear());
  const seq   = customSuffix || now.getTime().toString().slice(-5);

  switch (standardCode) {
    case 'UK_EA':    return `RB-UK/${yy}/${type}-${seq}`;
    case 'AU_EPA':   return `RB-AU/${yy}/${type}-${seq}`;
    case 'SA_WATER': return `RB-SA/${yy}/${type}-${seq}`;
    case 'EU_UWWTD': return `RB-EU/${yy}/${type}-${seq}`;
    case 'US_EPA':   return `RB-US/NPDES/${yy}/${type}-${seq}`;
    default:         return `RB/${yy}/${type}-${seq}`;
  }
}

// ─── Regulatory banner helper (2 rows, dynamic height, no overflow) ───────────
function drawRegulatoryBanner(
  doc: jsPDF,
  y: number,
  margin: number,
  cW: number,
  std: { name: string; permitType: string; report: { certificationMark: string; legalReference: string } },
): number {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  const line1 = `${std.name}  ·  ${std.permitType}`;
  const line2 = `${std.report.certificationMark}  ·  ${std.report.legalReference}`;
  const l1Lines = doc.splitTextToSize(line1, cW - 8);
  const l2Lines = doc.splitTextToSize(line2, cW - 8);
  const totalLines = l1Lines.length + l2Lines.length;
  const bannerH = 6 + totalLines * 4.5;

  doc.setFillColor(240, 249, 255);
  doc.setDrawColor(147, 197, 253);
  doc.roundedRect(margin, y, cW, bannerH, 2, 2, 'FD');

  doc.setFontSize(8);
  doc.setTextColor(30, 64, 175);
  doc.text(l1Lines, margin + 3, y + 5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(71, 85, 105);
  doc.text(l2Lines, margin + 3, y + 5 + l1Lines.length * 4.5);

  return y + bannerH + 4;
}

// ─── Section heading helper ────────────────────────────────────────────────────
function sectionHead(doc: jsPDF, y: number, margin: number, pageW: number, text: string): number {
  const cW = pageW - margin * 2;
  doc.setFillColor(...C.navyMid);
  doc.rect(margin, y, cW, 7, 'F');
  // Gold left accent strip
  doc.setFillColor(...C.gold);
  doc.rect(margin, y, 2.5, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...C.white);
  doc.text(text.toUpperCase(), margin + 5, y + 5);
  return y + 10;
}

function trendArrow(trend: string): string {
  if (trend === 'improving') return '↓ Improving';
  if (trend === 'deteriorating') return '↑ Deteriorating';
  if (trend === 'volatile') return '~ Volatile';
  return '→ Stable';
}

// ─── Parameter grouping for compliance tables ─────────────────────────────────
const PARAM_GROUP_ORDER = ['CORE PARAMETERS', 'NUTRIENTS', 'PRIORITY METALS', 'PHYSICAL / OTHER', 'OTHER'];
const PARAM_GROUP_MAP: Record<string, string> = {
  bod5: 'CORE PARAMETERS', bod: 'CORE PARAMETERS', cod: 'CORE PARAMETERS',
  tss: 'CORE PARAMETERS', ph: 'CORE PARAMETERS',
  nh3n: 'NUTRIENTS', ammonian: 'NUTRIENTS', totalnitrogen: 'NUTRIENTS',
  totalphosphorus: 'NUTRIENTS', tn: 'NUTRIENTS', tp: 'NUTRIENTS', no3n: 'NUTRIENTS',
  lead: 'PRIORITY METALS', mercury: 'PRIORITY METALS', cadmium: 'PRIORITY METALS',
  chromium: 'PRIORITY METALS', arsenic: 'PRIORITY METALS', nickel: 'PRIORITY METALS',
  zinc: 'PRIORITY METALS', copper: 'PRIORITY METALS', iron: 'PRIORITY METALS',
  manganese: 'PRIORITY METALS',
  temperature: 'PHYSICAL / OTHER', conductivity: 'PHYSICAL / OTHER', tds: 'PHYSICAL / OTHER',
  oilgrease: 'PHYSICAL / OTHER', oilgreaser: 'PHYSICAL / OTHER', turbidity: 'PHYSICAL / OTHER',
  chloride: 'PHYSICAL / OTHER', sulfide: 'PHYSICAL / OTHER', phenol: 'PHYSICAL / OTHER',
  surfactants: 'PHYSICAL / OTHER', hardness: 'PHYSICAL / OTHER', alkalinity: 'PHYSICAL / OTHER',
  mlss: 'PHYSICAL / OTHER',
};
function getParamGroup(key: string): string {
  return PARAM_GROUP_MAP[key.toLowerCase()] || 'OTHER';
}

/** Build a grouped body array for autoTable — inserts navy/gold group header rows */
function buildGroupedRows(
  entries: Array<string[]>,
  keyCol: number,  // which column index holds the parameter key
): Array<any[]> {
  const buckets: Record<string, string[][]> = {};
  for (const row of entries) {
    const grp = getParamGroup(row[keyCol]);
    if (!buckets[grp]) buckets[grp] = [];
    buckets[grp].push(row);
  }
  const out: Array<any[]> = [];
  for (const grp of PARAM_GROUP_ORDER) {
    const rows = buckets[grp];
    if (!rows?.length) continue;
    // Group header spanning all columns (determined by first row's length)
    const colCount = entries[0]?.length || 5;
    out.push([{ content: grp, colSpan: colCount,
      styles: { fillColor: C.navy, textColor: C.gold, fontStyle: 'bold', fontSize: 7.5, cellPadding: 2, halign: 'left' } }]);
    for (const r of rows) out.push(r);
  }
  // Anything without a group goes at the end
  if (buckets['OTHER']?.length) {
    const colCount = entries[0]?.length || 5;
    out.push([{ content: 'OTHER', colSpan: colCount,
      styles: { fillColor: C.navy, textColor: C.gold, fontStyle: 'bold', fontSize: 7.5, cellPadding: 2, halign: 'left' } }]);
    for (const r of buckets['OTHER']) out.push(r);
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════════
//  REPORT 1 — Multi-Sample Assessment Report  (replaces window.print in ReportsTab)
// ═══════════════════════════════════════════════════════════════════════════════

export interface AssessmentReportOptions {
  /** Optional site fields — populated when customer has a linked site */
  siteName?: string;
  siteAddress?: string;
  permitRef?: string;
  regulatoryAuthority?: string;
  preparedBy?: string;
  /** Regulatory standard code (e.g. 'UK_EA', 'US_EPA', 'EU_UWWTD', 'SA_WATER').
   *  Drives the standard banner, limit-comparison columns and stamps. */
  standard?: string;
}

export function generateAssessmentReportPDF(
  report: MultiSampleReport & { reportId?: string },
  opts: AssessmentReportOptions = {},
) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 18;
  const cW = pageW - margin * 2;

  // Ensure white background
  doc.setFillColor(...C.white);
  doc.rect(0, 0, pageW, pageH, 'F');

  // Resolve standard
  const std = ALL_STANDARDS.find(s => s.code === report.standard) || null;
  const stdText    = std ? `${std.shortName} — ${std.country}` : (report.standard || 'UK EA');
  const stdLegal   = std?.report.legalReference || '';
  const stdNote    = std?.report.complianceStatement || 'This report constitutes an early-stage engineering assessment only.';
  const stdLabel   = std?.report.dischargeStandardLabel || stdText;
  const regBody    = opts.regulatoryAuthority || std?.regulatoryBody || stdText;
  const reportRef  = report.reportId ? `RB-AR-${report.reportId}` : `RB-AR-${Date.now()}`;
  const genDate    = fmtDate(report.generatedAt);

  // ─────────────────────────── PAGE 1 ───────────────────────────────────────
  let y = drawPageHeader(doc, pageW, margin, stdText, reportRef);

  // Title block
  doc.setFillColor(...C.greyBg);
  doc.rect(margin, y, cW, 18, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...C.navy);
  doc.text('WASTEWATER QUALITY ASSESSMENT REPORT', margin + 4, y + 7);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...C.grey);
  doc.text(`Multi-Sample Trend Analysis  ·  ${report.sampleCount} samples  ·  ${report.dateRange.from} — ${report.dateRange.to}`, margin + 4, y + 13);
  y += 22;

  // ── Meta / document control table ─────────────────────────────────────────
  const metaRows: [string, string][] = [
    ['Client / Organisation:', report.customerName],
    ['Sampling Period:', `${report.dateRange.from} — ${report.dateRange.to}`],
    ['Samples Analysed:', String(report.sampleCount)],
    ['Report Generated:', genDate],
    ['Prepared by:', opts.preparedBy || 'Reynolds & Bauhm Ltd'],
    ['Discharge Standard:', stdLabel],
  ];
  if (opts.siteName)     metaRows.push(['Discharge Site:', opts.siteName]);
  if (opts.siteAddress)  metaRows.push(['Site / Location:', opts.siteAddress]);
  if (opts.permitRef)    metaRows.push(['Permit / Consent Ref:', opts.permitRef]);
  if (regBody)           metaRows.push(['Regulatory Authority:', regBody]);
  if (stdLegal)          metaRows.push(['Legal Reference:', stdLegal]);
  metaRows.push(['Analysis Confidence:', report.confidence.charAt(0).toUpperCase() + report.confidence.slice(1)]);

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    tableWidth: cW,
    theme: 'plain',
    styles: { fontSize: 9, font: 'helvetica', cellPadding: 2.5 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 55, textColor: C.grey },
      1: { textColor: C.black },
    },
    body: metaRows,
    willDrawCell: (data: any) => {
      // v5 has no didDrawRow; paint zebra stripe once per row (column 0) BEFORE
      // the cell text renders so the text stays on top.
      if (data.section === 'body' && data.column.index === 0 && data.row.index % 2 === 0) {
        doc.setFillColor(...C.greyBg);
        doc.rect(data.cell.x, data.cell.y, cW, data.row.height, 'F');
      }
    },
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

  // ── Regulatory Standard Banner ─────────────────────────────────────────────
  if (std) { y = drawRegulatoryBanner(doc, y, margin, cW, std); }

  // ── Executive Summary ──────────────────────────────────────────────────────
  y = sectionHead(doc, y, margin, pageW, 'Executive Summary');
  const summaryLines = doc.splitTextToSize(report.summary, cW - 6);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...C.black);
  doc.text(summaryLines, margin + 3, y + 1);
  y += summaryLines.length * 4.5 + 4;

  // ── KPI Strip ─────────────────────────────────────────────────────────────
  const kpiW = cW / 4;
  const kpis = [
    { label: 'Overall Compliance', value: `${report.overallCompliance}%`,
      color: report.overallCompliance >= 80 ? C.emerald : report.overallCompliance >= 50 ? C.amber : C.red },
    { label: 'Samples Analysed',   value: String(report.sampleCount), color: C.navy },
    { label: 'Critical Params',    value: String(report.criticalParameters.length),
      color: report.criticalParameters.length === 0 ? C.emerald : C.red },
    { label: 'Compliance Trend',
      value: report.complianceTrend.charAt(0).toUpperCase() + report.complianceTrend.slice(1),
      color: report.complianceTrend === 'improving' ? C.emerald : report.complianceTrend === 'deteriorating' ? C.red : C.grey },
  ];
  kpis.forEach((k, i) => {
    const x = margin + i * kpiW;
    doc.setFillColor(...C.greyBg);
    doc.setDrawColor(...C.greyLt);
    doc.rect(x, y, kpiW - 1, 14, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...k.color);
    doc.text(k.value, x + kpiW / 2, y + 8, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...C.grey);
    doc.text(k.label, x + kpiW / 2, y + 12.5, { align: 'center' });
  });
  y += 18;

  // ── Parameter Trend Analysis ──────────────────────────────────────────────
  y = sectionHead(doc, y, margin, pageW, 'Parameter Trend Analysis');

  const trendBody = report.parameterTrends.map(t => [
    `${t.label} (${t.unit})`,
    t.average > 0 ? t.average.toFixed(2) : '—',
    t.latestValue > 0 ? t.latestValue.toFixed(2) : '—',
    t.limit > 0 ? t.limit.toFixed(2) : '—',
    trendArrow(t.trend),
    t.pass ? 'PASS' : 'FAIL',
  ]);

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    tableWidth: cW,
    head: [['Parameter', 'Average', 'Latest', 'Limit', 'Trend', 'Status']],
    body: trendBody,
    headStyles: { fillColor: C.navyMid, textColor: C.white, fontStyle: 'bold', fontSize: 8, cellPadding: 3 },
    styles: { fontSize: 8, cellPadding: 2.5 },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 22, halign: 'right' as const },
      2: { cellWidth: 22, halign: 'right' as const },
      3: { cellWidth: 22, halign: 'right' as const },
      4: { cellWidth: 36 },
      5: { cellWidth: 18, halign: 'center' as const, fontStyle: 'bold' },
    },
    alternateRowStyles: { fillColor: C.greyBg },
    theme: 'grid',
    didParseCell: (data: any) => {
      if (data.column.index === 5 && data.section === 'body') {
        const pass = data.cell.raw === 'PASS';
        data.cell.styles.textColor = pass ? C.emerald : C.red;
        data.cell.styles.fillColor = pass ? C.emeraldBg : C.redBg;
      }
      if (data.column.index === 4 && data.cell.raw?.includes('Deteriorating')) {
        data.cell.styles.textColor = C.red;
      }
    },
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 4;

  // ─────────────────────────── PAGE 2 ───────────────────────────────────────
  doc.addPage();
  doc.setFillColor(...C.white);
  doc.rect(0, 0, pageW, pageH, 'F');
  y = drawPageHeader(doc, pageW, margin, stdText, reportRef);
  y += 2;

  // ── Process Train ─────────────────────────────────────────────────────────
  y = sectionHead(doc, y, margin, pageW, 'Recommended Treatment Process Train');

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    tableWidth: cW,
    head: [['Stage', 'Process', 'Purpose', 'Justification', 'Rec.']],
    body: report.processTrain.map((s: ProcessTrainRecommendation) => [
      String(s.stage),
      s.process,
      s.purpose,
      s.reason,
      s.recommended ? '✓' : '—',
    ]),
    headStyles: { fillColor: C.navyMid, textColor: C.white, fontStyle: 'bold', fontSize: 8, cellPadding: 3 },
    styles: { fontSize: 8, cellPadding: 2.5 },
    columnStyles: {
      0: { cellWidth: 12, halign: 'center' as const },
      1: { cellWidth: 38 },
      2: { cellWidth: 42 },
      3: { cellWidth: 'auto' },
      4: { cellWidth: 12, halign: 'center' as const, fontStyle: 'bold' },
    },
    alternateRowStyles: { fillColor: C.greyBg },
    theme: 'grid',
    didParseCell: (data: any) => {
      if (data.column.index === 4) {
        data.cell.styles.textColor = data.cell.raw === '✓' ? C.emerald : C.grey;
      }
      if (data.column.index === 1 && data.row.index >= 0 && data.section === 'body') {
        const rec = report.processTrain[data.row.index]?.recommended;
        if (rec) data.cell.styles.fontStyle = 'bold';
      }
    },
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

  // ── Chemical Dosing ────────────────────────────────────────────────────────
  if (report.dosingRecommendations.length > 0) {
    y = sectionHead(doc, y, margin, pageW, 'Proposed Chemical Dosing');

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      tableWidth: cW,
      head: [['Chemical', 'Purpose', 'Dose Range', 'Frequency', 'Based On']],
      body: report.dosingRecommendations.map((d: DosingRecommendation) => [
        d.chemical, d.purpose, d.doseRange, d.frequency, d.basedOn,
      ]),
      headStyles: { fillColor: C.gold, textColor: C.navy, fontStyle: 'bold', fontSize: 8, cellPadding: 3 },
      styles: { fontSize: 8, cellPadding: 2.5 },
      columnStyles: {
        0: { cellWidth: 30, fontStyle: 'bold' },
        1: { cellWidth: 40 },
        2: { cellWidth: 25, halign: 'center' as const },
        3: { cellWidth: 28 },
        4: { cellWidth: 'auto' },
      },
      alternateRowStyles: { fillColor: C.greyBg },
      theme: 'grid',
    });
    y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;
  }

  // ── Critical Parameters ────────────────────────────────────────────────────
  if (report.criticalParameters.length > 0) {
    y = sectionHead(doc, y, margin, pageW, 'Parameters Requiring Immediate Attention');

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      tableWidth: cW,
      head: [['Parameter', 'Average', 'Latest', 'Limit', 'Compliance %', 'Trend']],
      body: report.criticalParameters.slice(0, 10).map((p: ParameterTrend) => [
        `${p.label} (${p.unit})`,
        p.average.toFixed(2),
        p.latestValue.toFixed(2),
        p.limit > 0 ? p.limit.toFixed(2) : '—',
        `${p.compliance}%`,
        trendArrow(p.trend),
      ]),
      headStyles: { fillColor: C.red, textColor: C.white, fontStyle: 'bold', fontSize: 8, cellPadding: 3 },
      styles: { fontSize: 8, cellPadding: 2.5, textColor: C.black },
      columnStyles: {
        0: { cellWidth: 44 },
        1: { cellWidth: 22, halign: 'right' as const },
        2: { cellWidth: 22, halign: 'right' as const },
        3: { cellWidth: 22, halign: 'right' as const },
        4: { cellWidth: 26, halign: 'center' as const, fontStyle: 'bold', textColor: C.red },
        5: { cellWidth: 'auto' },
      },
      alternateRowStyles: { fillColor: [255, 245, 245] as [number,number,number] },
      theme: 'grid',
    });
    y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;
  }

  // ── Discharge Limits Applied ───────────────────────────────────────────────
  if (std) {
    // check if we have enough space; if not, new page
    if (y > pageH - 90) {
      doc.addPage();
      doc.setFillColor(...C.white);
      doc.rect(0, 0, pageW, pageH, 'F');
      y = drawPageHeader(doc, pageW, margin, stdText, reportRef) + 2;
    }
    y = sectionHead(doc, y, margin, pageW, `${std.labels.dischargeLimitsTitle} — Limits Applied`);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...C.grey);
    doc.text(std.labels.dischargeLimitsSubtitle, margin + 2, y);
    y += 5;

    const limitRows = [
      ['COD',              `${std.limits.cod} mg/L`,            'Oil & Grease',     `${std.limits.oilGrease} mg/L`],
      ['BOD5',            `${std.limits.bod5} mg/L`,            'Total Nitrogen',   `${std.limits.totalNitrogen} mg/L`],
      ['TSS',             `${std.limits.tss} mg/L`,             'Total Phosphorus', `${std.limits.totalPhosphorus} mg/L`],
      [`pH`,              `${std.limits.phMin}–${std.limits.phMax}`, 'Ammonia-N',  `${std.limits.ammoniaN} mg/L`],
      ['Lead (Pb)',       `${std.limits.lead} mg/L`,            'Chromium (Cr)',    `${std.limits.chromium} mg/L`],
      ['Temperature',    `${std.limits.temperature} °C max`, 'Conductivity',     `${std.limits.conductivity} µS/cm`],
    ];

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      tableWidth: cW,
      head: [['Parameter', 'Limit', 'Parameter', 'Limit']],
      body: limitRows,
      headStyles: { fillColor: C.navyMid, textColor: C.white, fontStyle: 'bold', fontSize: 8, cellPadding: 3 },
      styles: { fontSize: 8, cellPadding: 2.5 },
      columnStyles: {
        0: { cellWidth: 38, fontStyle: 'bold' },
        1: { cellWidth: 30, halign: 'right' as const, fontStyle: 'bold', textColor: C.navyMid },
        2: { cellWidth: 38, fontStyle: 'bold' },
        3: { cellWidth: 'auto', halign: 'right' as const, fontStyle: 'bold', textColor: C.navyMid },
      },
      alternateRowStyles: { fillColor: C.greyBg },
      theme: 'grid',
    });
    y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;
  }

  // ── IP / Compliance Disclaimer ─────────────────────────────────────────────
  if (y > pageH - 50) {
    doc.addPage();
    doc.setFillColor(...C.white);
    doc.rect(0, 0, pageW, pageH, 'F');
    y = drawPageHeader(doc, pageW, margin, stdText, reportRef) + 4;
  }
  doc.setFillColor(255, 251, 235);
  doc.setDrawColor(205, 164, 46);
  doc.roundedRect(margin, y, cW, 22, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(146, 64, 14);
  doc.text('IMPORTANT — REGULATORY NOTICE', margin + 3, y + 5.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(92, 63, 10);
  const disclaimer = `This report and all assessment algorithms are the exclusive intellectual property of Reynolds & Bauhm Ltd. ` +
    `Results are indicative only and based on the sample data provided. ` +
    `${stdNote} ` +
    `Laboratory testing, site-specific consent conditions, and consultation with a licensed wastewater engineer are required ` +
    `before any regulatory submission or engineering decision. Reynolds & Bauhm Ltd accepts no liability for decisions made solely on the basis of this report.`;
  const discLines = doc.splitTextToSize(disclaimer, cW - 6);
  doc.text(discLines, margin + 3, y + 10);
  y += 28;

  // Certification footer strip
  doc.setFillColor(...C.greyBg);
  doc.setDrawColor(...C.greyLt);
  doc.rect(margin, y, cW, 10, 'FD');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...C.grey);
  doc.text(`Document ID: ${reportRef}  ·  Generated: ${genDate}  ·  Standard: ${stdLabel}  ·  Confidence: ${report.confidence}`, margin + 3, y + 6);

  // ── Watermarks + Footers ────────────────────────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  stampAllPages(doc, pageW, pageH, opts.standard);
  drawFooters(doc, pageCount, pageW, pageH, margin, reportRef, stdLabel, stdNote);

  // Save
  const safeCust = report.customerName.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 24);
  doc.save(`${reportRef}_${safeCust}_AssessmentReport.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
//  REPORT 2 — Sample Trend Report  (replaces window.print in SampleReportPrint)
// ═══════════════════════════════════════════════════════════════════════════════

export interface SampleForPDF {
  name: string;
  /** Regulatory reference ID shown under the name — e.g. RB-AU/2026/INF-94168 */
  referenceId?: string;
  date: Date | string;
  params: Record<string, number>;
  reportNumber?: string;
}

export interface SampleTrendReportOptions {
  reportNumber: string;
  reportTitle: string;
  customerName?: string;
  siteName?: string;
  siteAddress?: string;
  permitRef?: string;
  regulatoryAuthority?: string;
  standard?: string;
  preparedBy?: string;
  createdAt?: Date | string;
  /** Optional: monitoring point / discharge point identifier */
  monitoringPoint?: string;
  /** Optional: lab name and accreditation number */
  labName?: string;
  labCertNumber?: string;
  /** Optional: authorised signatory name / title */
  signatoryName?: string;
  signatoryTitle?: string;
}

// ─── Analysis method references per parameter per standard ─────────────────────
const ANALYSIS_METHODS: Record<string, Record<string, string>> = {
  UK_EA: {
    bod: 'BS EN 1899-1:1998',   cod: 'BS EN ISO 6060:1996',
    tss: 'BS EN 872:2005',      ph: 'BS EN ISO 10523:2012',
    nh3: 'BS EN ISO 11732:2005',og: 'IP 404 / BS 7402',
    oandg: 'IP 404 / BS 7402',  temperature: 'BS EN ISO 10523',
    conductivity: 'BS EN 27888',tds: 'BS EN ISO 17294',
    lead: 'BS EN ISO 11885',     chromium: 'BS EN ISO 11885',
    turbidity: 'BS EN ISO 7027',
  },
  AU_EPA: {
    bod: 'APHA 5210B',      cod: 'APHA 5220C',
    tss: 'APHA 2540D',      ph: 'APHA 4500-H+B',
    nh3: 'APHA 4500-NH3C',  og: 'APHA 5520B',
    oandg: 'APHA 5520B',    temperature: 'APHA 2550B',
    conductivity: 'APHA 2510B', tds: 'APHA 2540C',
    lead: 'APHA 3111B',         chromium: 'APHA 3111C',
    turbidity: 'APHA 2130B',
  },
  EU_UWWTD: {
    bod: 'EN 1899-1',       cod: 'EN ISO 6060',
    tss: 'EN 872',          ph: 'EN ISO 10523',
    nh3: 'EN ISO 11732',    og: 'EN ISO 9377-2',
    oandg: 'EN ISO 9377-2', temperature: 'ISO 10523',
    conductivity: 'EN 27888', tds: 'EN ISO 17294',
    totalNitrogen: 'EN ISO 11905', totalPhosphorus: 'EN ISO 6878',
    lead: 'EN ISO 11885',   chromium: 'EN ISO 11885',
    turbidity: 'EN ISO 7027',
  },
  US_EPA: {
    bod: 'SM 5210B / EPA 405.1',    cod: 'SM 5220C / EPA 410.4',
    tss: 'SM 2540D / EPA 160.2',    ph: 'SM 4500-H+B / EPA 150.1',
    nh3: 'SM 4500-NH3C / EPA 350.1',og: 'EPA 1664A',
    oandg: 'EPA 1664A',             temperature: 'EPA 170.1',
    conductivity: 'SM 2510B / EPA 120.1', tds: 'SM 2540C / EPA 160.1',
    lead: 'EPA 200.7 / EPA 6010C',  chromium: 'EPA 200.7 / EPA 6010C',
    turbidity: 'SM 2130B / EPA 180.1',
  },
};

function getAnalysisMethod(param: string, standard: string): string {
  const stdMethods = ANALYSIS_METHODS[standard] || ANALYSIS_METHODS['UK_EA'];
  return stdMethods[param.toLowerCase()] || stdMethods[param] || '—';
}

// ─── Country-specific accreditation / lab notice ───────────────────────────────
function getLabNotice(standard: string): string {
  switch (standard) {
    case 'UK_EA':    return 'Analysis performed by MCERTS-accredited laboratory in accordance with Environment Agency requirements (Environmental Permitting (England and Wales) Regulations 2016).';
    case 'AU_EPA':   return 'Analysis performed by NATA-accredited laboratory (National Association of Testing Authorities, Australia) in accordance with NSW EPA/POEO Act 1997 requirements.';
    case 'EU_UWWTD': return 'Analysis performed by accredited laboratory in accordance with Urban Wastewater Treatment Directive 91/271/EEC (as amended by Directive 2024/3019/EU) and applicable EN/ISO standard methods.';
    case 'US_EPA':   return 'Analysis performed using methods approved under 40 CFR Part 136 by a laboratory certified under the applicable State certification program.';
    default:         return 'Analysis performed by accredited laboratory in accordance with applicable standard methods.';
  }
}

// ─── Country-specific compliance statement heading ─────────────────────────────
function getComplianceHeading(standard: string): string {
  switch (standard) {
    case 'UK_EA':    return 'Environmental Permit (EPR) Compliance Summary';
    case 'AU_EPA':   return 'Environmental Protection Licence (EPL) Compliance Summary';
    case 'EU_UWWTD': return 'UWWTD 91/271/EEC Compliance Assessment';
    case 'US_EPA':   return 'NPDES Permit Compliance Summary — 40 CFR Part 122';
    default:         return 'Regulatory Compliance Summary';
  }
}

export function generateSampleTrendReportPDF(
  samples: SampleForPDF[],
  opts: SampleTrendReportOptions,
  mode: 'save' | 'preview' = 'save',
): string | undefined {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 18;
  const cW = pageW - margin * 2;

  doc.setFillColor(...C.white);
  doc.rect(0, 0, pageW, pageH, 'F');

  const std = opts.standard ? ALL_STANDARDS.find(s => s.code === opts.standard) || null : null;
  const stdText  = std ? `${std.shortName} — ${std.country}` : (opts.standard || 'UK EA');
  const stdLabel = std?.report.dischargeStandardLabel || stdText;
  const stdNote  = std?.report.complianceStatement || 'Early assessment only — not for regulatory submission without professional review.';
  const genDate  = fmtDate(opts.createdAt || new Date());

  const sorted = [...samples].sort((a, b) => new Date(a.date as string).getTime() - new Date(b.date as string).getTime());

  // All parameter keys across all samples
  const allKeys = Array.from(new Set(sorted.flatMap(s => Object.keys(s.params).filter(k => (s.params[k] ?? 0) > 0))));

  // ─── PAGE 1 ──────────────────────────────────────────────────────────────
  let y = drawPageHeader(doc, pageW, margin, stdText, opts.reportNumber);

  // Title block
  doc.setFillColor(...C.greyBg);
  doc.rect(margin, y, cW, 18, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...C.navy);
  doc.text('WASTEWATER SAMPLE TREND REPORT', margin + 4, y + 7);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...C.grey);
  doc.text(opts.reportTitle || 'Sample Analysis', margin + 4, y + 13);
  y += 22;

  // Meta table
  const metaRows: [string, string][] = [
    ['Report Reference:', opts.reportNumber],
    ['Issue Date:', genDate],
    ['Prepared by:', opts.preparedBy || 'Reynolds & Bauhm Ltd'],
    ['Samples Analysed:', String(samples.length)],
    ['Date Range:', sorted.length ? `${fmtDate(sorted[0].date)} — ${fmtDate(sorted[sorted.length - 1].date)}` : '—'],
  ];
  if (opts.customerName)        metaRows.push(['Client:', opts.customerName]);
  if (opts.siteName)            metaRows.push(['Discharge Site:', opts.siteName]);
  if (opts.siteAddress)         metaRows.push(['Location:', opts.siteAddress]);
  if (opts.permitRef)           metaRows.push(['Permit / Consent Ref:', opts.permitRef]);
  // Always show regulatory authority — use explicitly provided value, else derive from standard
  const regAuthority = opts.regulatoryAuthority || std?.regulatoryBody;
  if (regAuthority) metaRows.push(['Regulatory Authority:', regAuthority]);
  if (std?.permitType) metaRows.push(['Permit Type:', std.permitType]);
  metaRows.push(['Discharge Standard:', stdLabel]);
  if (std?.report.legalReference) metaRows.push(['Legal Reference:', std.report.legalReference]);
  if (std?.report.certificationMark) metaRows.push(['Accreditation:', std.report.certificationMark]);

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    tableWidth: cW,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 2.5 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 55, textColor: C.grey },
      1: { textColor: C.black },
    },
    body: metaRows,
    willDrawCell: (data: any) => {
      // v5 has no didDrawRow; paint zebra stripe once per row (column 0) BEFORE
      // the cell text renders so the text stays on top.
      if (data.section === 'body' && data.column.index === 0 && data.row.index % 2 === 0) {
        doc.setFillColor(...C.greyBg);
        doc.rect(data.cell.x, data.cell.y, cW, data.row.height, 'F');
      }
    },
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

  // Regulatory banner
  if (std) { y = drawRegulatoryBanner(doc, y, margin, cW, std); }

  // ── Sample Data Table ────────────────────────────────────────────────────
  y = sectionHead(doc, y, margin, pageW, 'Sample Parameter Data — Individual Measurements');

  // Columns: No. | Sample Name / Reference ID | Date | param1 | param2 | ...
  // Regulatory requirement: sequential IDs + full untruncated names + reference IDs
  const snColW   = 10;  // "No." column
  const nameColW = 72;  // name + reference ID on second line — no truncation, extra width
  const dateColW = 22;
  const fixedW   = snColW + nameColW + dateColW;
  const minParamColW = 10;
  const maxParamCols = Math.floor((cW - fixedW) / minParamColW);
  const paramCols    = allKeys.slice(0, Math.min(11, maxParamCols));
  const paramColW    = paramCols.length > 0 ? Math.floor((cW - fixedW) / paramCols.length) : minParamColW;

  const fmtParamHead = (k: string) =>
    k.toUpperCase()
     .replace('OILGREASE', 'O&G').replace('OANDG', 'O&G')
     .replace('AMMONIAN', 'NH3-N').replace('NH3N', 'NH3-N').replace('NH3', 'NH3')
     .replace('TOTALNITROGEN', 'TN').replace('TOTALPHOSPHORUS', 'TP')
     .replace('CONDUCTIVITY', 'COND').replace('TURBIDITY', 'TURB')
     .replace('TEMPERATURE', 'TEMP').replace('HARDNESS', 'HARD');

  const tableHead = [['No.', 'Sample Name / ID', 'Sample Date',
    ...paramCols.map(fmtParamHead)]];

  const tableBody = sorted.map((s, idx) => [
    `S-${String(idx + 1).padStart(3, '0')}`,  // regulatory sequential ID: S-001, S-002…
    s.referenceId ? `${s.name}\n${s.referenceId}` : s.name,  // name + reference ID
    fmtDate(s.date),
    ...paramCols.map(k => {
      const v = s.params[k];
      return (typeof v === 'number' && v > 0) ? v.toFixed(k === 'ph' ? 2 : 1) : '—';
    }),
  ]);

  const colStyles: Record<number, any> = {
    0: { cellWidth: snColW,   halign: 'center' as const, fontStyle: 'bold', textColor: C.navyMid },
    1: { cellWidth: nameColW, fontStyle: 'bold', overflow: 'linebreak' as const, fontSize: 6.5 },
    2: { cellWidth: dateColW },
  };
  paramCols.forEach((_, i) => {
    colStyles[i + 3] = { cellWidth: paramColW, halign: 'right' as const };
  });

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    tableWidth: cW,
    head: tableHead,
    body: tableBody,
    headStyles: { fillColor: C.navyMid, textColor: C.white, fontStyle: 'bold', fontSize: 7, cellPadding: 2 },
    styles: { fontSize: 7, cellPadding: 1.8, overflow: 'linebreak' as const },
    columnStyles: colStyles,
    alternateRowStyles: { fillColor: C.greyBg },
    theme: 'grid',
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

  // Overflow table for remaining params (if any)
  if (allKeys.length > paramCols.length) {
    const overflow  = allKeys.slice(paramCols.length);
    const ovMaxCols = Math.floor((cW - fixedW) / minParamColW);
    const ovCols    = overflow.slice(0, ovMaxCols);
    const ovColW    = Math.floor((cW - fixedW) / ovCols.length);
    const ovHead    = [['No.', 'Sample Name / ID', 'Sample Date', ...ovCols.map(fmtParamHead)]];
    const ovBody    = sorted.map((s, idx) => [
      `S-${String(idx + 1).padStart(3, '0')}`,
      s.referenceId ? `${s.name}\n${s.referenceId}` : s.name,
      fmtDate(s.date),
      ...ovCols.map(k => { const v = s.params[k]; return (typeof v === 'number' && v > 0) ? v.toFixed(k === 'ph' ? 2 : 1) : '—'; }),
    ]);
    const ovStyles: Record<number, any> = {
      0: { cellWidth: snColW, halign: 'center' as const, fontStyle: 'bold', textColor: C.navyMid },
      1: { cellWidth: nameColW, fontStyle: 'bold', overflow: 'linebreak' as const, fontSize: 6.5 },
      2: { cellWidth: dateColW },
    };
    ovCols.forEach((_, i) => { ovStyles[i + 3] = { cellWidth: ovColW, halign: 'right' as const }; });
    if (y > pageH - 60) {
      doc.addPage();
      doc.setFillColor(...C.white);
      doc.rect(0, 0, pageW, pageH, 'F');
      y = drawPageHeader(doc, pageW, margin, stdText, opts.reportNumber) + 4;
    }
    y = sectionHead(doc, y, margin, pageW, 'Sample Parameter Data (continued)');
    autoTable(doc, {
      startY: y, margin: { left: margin, right: margin }, tableWidth: cW,
      head: ovHead, body: ovBody,
      headStyles: { fillColor: C.navyMid, textColor: C.white, fontStyle: 'bold', fontSize: 7, cellPadding: 2 },
      styles: { fontSize: 7, cellPadding: 1.8, overflow: 'linebreak' as const },
      columnStyles: ovStyles,
      alternateRowStyles: { fillColor: C.greyBg },
      theme: 'grid',
    });
    y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;
  }

  // ── Parameter Averages Table ──────────────────────────────────────────────
  if (allKeys.length > 0) {
    if (y > pageH - 80) {
      doc.addPage();
      doc.setFillColor(...C.white);
      doc.rect(0, 0, pageW, pageH, 'F');
      y = drawPageHeader(doc, pageW, margin, stdText, opts.reportNumber) + 4;
    }
    y = sectionHead(doc, y, margin, pageW, 'Statistical Summary');

    const statRows = allKeys.map(k => {
      const vals      = sorted.map(s => s.params[k] ?? 0).filter(v => v > 0);
      const avg       = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
      const minV      = vals.length ? Math.min(...vals) : 0;
      const maxV      = vals.length ? Math.max(...vals) : 0;
      const unit      = getUnit(k);
      const stdLimit  = std ? (std.limits as any)[k.toLowerCase()] ?? '—' : '—';
      const method    = getAnalysisMethod(k, opts.standard || 'UK_EA');
      const passStatus = vals.length > 0 && typeof stdLimit === 'number'
        ? (avg <= stdLimit ? 'PASS' : 'FAIL')
        : '—';
      return [
        k.toUpperCase().replace('OANDG', 'O&G').replace('NH3N', 'NH3-N').replace('NH3', 'NH3-N'),
        unit,
        avg > 0 ? avg.toFixed(2) : '—',
        minV > 0 ? minV.toFixed(2) : '—',
        maxV > 0 ? maxV.toFixed(2) : '—',
        String(stdLimit),
        passStatus,
        method,
      ];
    });

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      tableWidth: cW,
      head: [['Parameter', 'Unit', 'Average', 'Min', 'Max', 'Limit', 'Status', 'Analysis Method (Standard Ref.)']],
      body: statRows,
      headStyles: { fillColor: C.navyMid, textColor: C.white, fontStyle: 'bold', fontSize: 7.5, cellPadding: 2.5 },
      styles: { fontSize: 7.5, cellPadding: 2.2, overflow: 'ellipsize' as const },
      columnStyles: {
        0: { cellWidth: 24, fontStyle: 'bold' },
        1: { cellWidth: 14 },
        2: { cellWidth: 16, halign: 'right' as const },
        3: { cellWidth: 16, halign: 'right' as const },
        4: { cellWidth: 16, halign: 'right' as const },
        5: { cellWidth: 16, halign: 'right' as const },
        6: { cellWidth: 16, halign: 'center' as const, fontStyle: 'bold' },
        7: { cellWidth: 'auto' as const, fontSize: 6.5, textColor: C.grey },
      },
      alternateRowStyles: { fillColor: C.greyBg },
      theme: 'grid',
      didParseCell: (data: any) => {
        if (data.column.index === 6) {
          if (data.cell.raw === 'PASS') { data.cell.styles.textColor = C.emerald; data.cell.styles.fillColor = C.emeraldBg; }
          else if (data.cell.raw === 'FAIL') { data.cell.styles.textColor = C.red; data.cell.styles.fillColor = C.redBg; }
        }
      },
    });
    y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;
  }

  // ── Laboratory Accreditation & Analysis Methods Notice ────────────────────
  if (y > pageH - 50) {
    doc.addPage();
    doc.setFillColor(...C.white);
    doc.rect(0, 0, pageW, pageH, 'F');
    y = drawPageHeader(doc, pageW, margin, stdText, opts.reportNumber) + 4;
  }
  {
    const labNotice = getLabNotice(opts.standard || 'UK_EA');
    const labLines  = doc.splitTextToSize(labNotice, cW - 8);
    const labH      = 10 + labLines.length * 3.8;
    doc.setFillColor(240, 249, 255); doc.setDrawColor(147, 197, 253);
    doc.roundedRect(margin, y, cW, labH, 2, 2, 'FD');
    doc.setFont('helvetica', 'bold');   doc.setFontSize(7.5);
    doc.setTextColor(30, 64, 175);
    doc.text('ACCREDITATION & ANALYTICAL METHODS', margin + 3, y + 5);
    if (opts.labName) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(30, 64, 175);
      doc.text(`Laboratory: ${opts.labName}${opts.labCertNumber ? '  |  Cert No: ' + opts.labCertNumber : ''}`, margin + 3, y + 5 + 4);
    }
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(71, 85, 105);
    doc.text(labLines, margin + 3, y + (opts.labName ? 12 : 9));
    y += labH + 4;
  }

  // ── Country-Specific Regulatory Compliance Section ─────────────────────────
  if (y > pageH - 80) {
    doc.addPage();
    doc.setFillColor(...C.white);
    doc.rect(0, 0, pageW, pageH, 'F');
    y = drawPageHeader(doc, pageW, margin, stdText, opts.reportNumber) + 4;
  }
  y = sectionHead(doc, y, margin, pageW, getComplianceHeading(opts.standard || 'UK_EA'));

  // Build compliance rows: one row per parameter with limit, avg, status, % of limit
  const compRows = allKeys.map((k, idx) => {
    const sampleId = `S-${String(1).padStart(3,'0')}–S-${String(sorted.length).padStart(3,'0')}`;
    const vals     = sorted.map(s => s.params[k] ?? 0).filter(v => v > 0);
    const avg      = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    const stdLimit = std ? (std.limits as any)[k.toLowerCase()] : undefined;
    const pctOfLim = (avg > 0 && typeof stdLimit === 'number' && stdLimit > 0)
      ? ((avg / stdLimit) * 100).toFixed(0) + '%'
      : '—';
    const statusRaw = vals.length > 0 && typeof stdLimit === 'number'
      ? (avg <= stdLimit ? 'COMPLIANT' : 'EXCEEDS LIMIT')
      : 'NO LIMIT SET';
    return [
      `S-${String(idx + 1).padStart(3,'0')}`,
      k.toUpperCase().replace('OANDG','O&G').replace('NH3N','NH3-N').replace('NH3','NH3-N'),
      getUnit(k),
      avg > 0 ? avg.toFixed(2) : '—',
      typeof stdLimit === 'number' ? String(stdLimit) : '—',
      pctOfLim,
      statusRaw,
    ];
  });

  const groupedCompRows = buildGroupedRows(compRows, 1); // key is in column index 1 (Parameter)
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    tableWidth: cW,
    head: [['Seq ID', 'Parameter', 'Unit', 'Measured Avg', 'Permit Limit', '% of Limit', 'Compliance Status']],
    body: groupedCompRows,
    headStyles: { fillColor: [15, 23, 42] as [number,number,number], textColor: C.gold, fontStyle: 'bold', fontSize: 8, cellPadding: 2.5 },
    styles: { fontSize: 8, cellPadding: 2.2 },
    columnStyles: {
      0: { cellWidth: 16, halign: 'center' as const, fontStyle: 'bold', textColor: C.navyMid },
      1: { cellWidth: 30, fontStyle: 'bold' },
      2: { cellWidth: 16 },
      3: { cellWidth: 26, halign: 'right' as const },
      4: { cellWidth: 26, halign: 'right' as const },
      5: { cellWidth: 22, halign: 'center' as const },
      6: { cellWidth: 'auto' as const, fontStyle: 'bold' },
    },
    alternateRowStyles: { fillColor: C.greyBg },
    theme: 'grid',
    didParseCell: (data: any) => {
      if (typeof data.cell.raw === 'object') return;
      if (data.column.index === 6 && data.section === 'body') {
        const v = String(data.cell.raw || '');
        if (v === 'COMPLIANT')          { data.cell.styles.textColor = C.emerald; data.cell.styles.fillColor = C.emeraldBg; }
        else if (v.startsWith('EXCEEDS')) { data.cell.styles.textColor = C.red;     data.cell.styles.fillColor = C.redBg; }
        else data.cell.styles.textColor = C.grey;
      }
      if (data.column.index === 5 && data.section === 'body') {
        const pct = parseInt(String(data.cell.raw || '0'));
        if (!isNaN(pct)) {
          data.cell.styles.textColor = pct >= 100 ? C.red : pct >= 80 ? C.amber : C.emerald;
          data.cell.styles.fontStyle = 'bold';
        }
      }
    },
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 4;

  // US EPA / NPDES: Legally required DMR certification statement (40 CFR §122.22)
  if (opts.standard === 'US_EPA') {
    const certText =
      'NPDES DMR CERTIFICATION (40 CFR §122.22): I certify under penalty of law that this document and all ' +
      'attachments were prepared under my direction or supervision in accordance with a system designed to assure that ' +
      'qualified personnel properly gathered and evaluated the information submitted. Based on my inquiry of the person ' +
      'or persons who manage the system, or those persons directly responsible for gathering the information, the ' +
      'information submitted is, to the best of my knowledge and belief, true, accurate, and complete. I am aware ' +
      'that there are significant penalties for submitting false information, including the possibility of fine and ' +
      'imprisonment for knowing violations. (Clean Water Act, Section 309)';
    const certLines = doc.splitTextToSize(certText, cW - 6);
    const certH = 8 + certLines.length * 3.6;
    if (y + certH > pageH - 20) {
      doc.addPage();
      doc.setFillColor(...C.white);
      doc.rect(0, 0, pageW, pageH, 'F');
      y = drawPageHeader(doc, pageW, margin, stdText, opts.reportNumber) + 4;
    }
    doc.setFillColor(239, 246, 255); doc.setDrawColor(59, 130, 246);
    doc.roundedRect(margin, y, cW, certH, 2, 2, 'FD');
    doc.setFont('helvetica', 'bold');   doc.setFontSize(7); doc.setTextColor(30, 64, 175);
    doc.text('LEGAL CERTIFICATION — REQUIRED BY 40 CFR §122.22', margin + 3, y + 4.5);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(30, 58, 138);
    doc.text(certLines, margin + 3, y + 8.5);
    y += certH + 4;
  }

  // EU UWWTD: Sampling frequency statement (Art. 15 requirement)
  if (opts.standard === 'EU_UWWTD') {
    const euNote = `UWWTD SAMPLING FREQUENCY (Directive 91/271/EEC, Annex I-D): Minimum sampling frequencies — ` +
      `agglomerations 2,000–10,000 PE: 12 samples/year; >10,000 PE: 24 samples/year. ` +
      `All sample results must be compared against Annex I-B effluent standards. ` +
      `This report covers ${sorted.length} sample${sorted.length !== 1 ? 's' : ''} within the reporting period.`;
    const euLines = doc.splitTextToSize(euNote, cW - 6);
    const euH     = 6 + euLines.length * 3.6;
    doc.setFillColor(245, 243, 255); doc.setDrawColor(139, 92, 246);
    doc.roundedRect(margin, y, cW, euH, 2, 2, 'FD');
    doc.setFont('helvetica', 'bold');   doc.setFontSize(7); doc.setTextColor(76, 29, 149);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(76, 29, 149);
    doc.text(euLines, margin + 3, y + 4.5);
    y += euH + 4;
  }

  // SA Water: Excess Concentration Charges table
  if (opts.standard === 'SA_WATER') {
    const saStd = ALL_STANDARDS.find(s => s.code === 'SA_WATER');
    const charges = saStd?.excessCharges;
    if (charges) {
      if (y > pageH - 70) {
        doc.addPage();
        doc.setFillColor(...C.white);
        doc.rect(0, 0, pageW, pageH, 'F');
        y = drawPageHeader(doc, pageW, margin, stdText, opts.reportNumber) + 4;
      }
      y = sectionHead(doc, y, margin, pageW, 'SA Water — Excess Concentration Charges (AUD/kg excl. GST)');

      // Trigger notice
      const triggerLines = doc.splitTextToSize(
        `CHARGE TRIGGER: ${charges.annualTrigger}. Charges are levied per kg of pollutant discharged above the applicable threshold. All rates exclude GST. Source: SA Water Fees & Charges Schedule.`,
        cW - 6
      );
      const triggerH = 6 + triggerLines.length * 3.6;
      doc.setFillColor(239, 246, 255); doc.setDrawColor(59, 130, 246);
      doc.roundedRect(margin, y, cW, triggerH, 2, 2, 'FD');
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(30, 64, 175);
      doc.text(triggerLines, margin + 3, y + 4.5);
      y += triggerH + 3;

      // Per-sample cost estimate rows
      const chargeBody = charges.charges.map(ch => {
        // Find average for this parameter across all samples
        const paramKey = ch.parameter === 'BOD' ? 'bod5'
          : ch.parameter === 'SS'  ? 'tss'
          : ch.parameter === 'TDS' ? 'tds'
          : ch.parameter === 'TKN' ? 'tn'
          : ch.parameter === 'TP'  ? 'totalPhosphorus'
          : ch.parameter.toLowerCase();
        const vals = sorted.map(s => s.params[paramKey] ?? s.params[ch.parameter.toLowerCase()] ?? 0).filter(v => v > 0);
        const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
        // Rough load estimate: assume 1 m³/day × number of samples as proxy
        const avgMgL = avg > 0 ? avg.toFixed(1) : '—';
        return [
          ch.parameter,
          ch.condition,
          `$${ch.ratePerKg.toFixed(3)}`,
          avgMgL !== '—' ? avgMgL : '—',
          ch.notes || '',
        ];
      });

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        tableWidth: cW,
        head: [['Parameter', 'Condition', 'Rate (AUD/kg)', 'Site Avg (mg/L)', 'Notes']],
        body: chargeBody,
        headStyles: { fillColor: [29, 78, 216] as [number,number,number], textColor: C.white, fontStyle: 'bold', fontSize: 7.5, cellPadding: 2.5 },
        styles: { fontSize: 7.5, cellPadding: 2.2 },
        columnStyles: {
          0: { cellWidth: 18, fontStyle: 'bold' },
          1: { cellWidth: 32 },
          2: { cellWidth: 28, halign: 'right' as const, fontStyle: 'bold', textColor: [30, 64, 175] as [number,number,number] },
          3: { cellWidth: 28, halign: 'right' as const },
          4: { cellWidth: 'auto' as const, textColor: C.grey, fontSize: 6.5 },
        },
        alternateRowStyles: { fillColor: C.greyBg },
        theme: 'grid',
        didParseCell: (data: any) => {
          if (data.section === 'body' && data.column.index === 2) {
            const rate = parseFloat(String(data.cell.raw).replace('$',''));
            if (rate >= 1.0) { data.cell.styles.textColor = C.red; }
          }
        },
      });
      y = ((doc as any).lastAutoTable?.finalY ?? y) + 4;
    }
  }

  // ── Parameter Trend Bar Charts ────────────────────────────────────────────
  // Drawn as horizontal bars in native jsPDF (no canvas dependency)
  // Shows average measured value vs discharge limit for key parameters
  {
    const chartParams = [
      { key: 'bod5',           label: 'BOD₅',           unit: 'mg/L' },
      { key: 'tss',            label: 'TSS',             unit: 'mg/L' },
      { key: 'oilGrease',      label: 'Oil & Grease',    unit: 'mg/L' },
      { key: 'totalPhosphorus',label: 'Total Phosphorus',unit: 'mg/L' },
      { key: 'tn',             label: 'TN / TKN',        unit: 'mg/L' },
      { key: 'tds',            label: 'TDS',             unit: 'mg/L' },
      { key: 'ph',             label: 'pH',              unit: '' },
      { key: 'conductivity',   label: 'Conductivity',    unit: 'µS/cm' },
      { key: 'temperature',    label: 'Temperature',     unit: '°C' },
    ].filter(cp => {
      const vals = sorted.map(s => s.params[cp.key] ?? s.params[cp.key.toLowerCase()] ?? 0).filter(v => v > 0);
      return vals.length > 0;
    });

    if (chartParams.length > 0) {
      // Always start charts on a fresh page so they are never split
      doc.addPage();
      doc.setFillColor(...C.white);
      doc.rect(0, 0, pageW, pageH, 'F');
      y = drawPageHeader(doc, pageW, margin, stdText, opts.reportNumber) + 4;
      y = sectionHead(doc, y, margin, pageW, 'Parameter Trend — Measured vs Discharge Limit');

      // Layout constants
      const labelW  = 36;   // parameter name column width (mm)
      const barAreaW = cW - labelW - 32; // bar drawing area
      const barH    = 7;    // height of each bar (mm)
      const rowGap  = 4;    // gap between rows (mm)
      const rowStep = barH + rowGap;
      const barX    = margin + labelW; // x start of bar area

      chartParams.forEach((cp, idx) => {
        // Compute average
        const vals = sorted.map(s => {
          return s.params[cp.key] ?? s.params[cp.key.toLowerCase()] ?? 0;
        }).filter(v => v > 0);
        if (!vals.length) return;

        const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
        const maxVal = Math.max(...vals);
        const stdLimit: number | undefined = std ? (std.limits as any)[cp.key] ?? (std.limits as any)[cp.key.toLowerCase()] : undefined;

        // Pct of limit (or pct of measured max if no limit)
        const refVal = typeof stdLimit === 'number' && stdLimit > 0 ? stdLimit : maxVal * 1.2;
        const pct    = Math.min(avg / refVal, 1.5); // cap at 150% for visual
        const barFillW = barAreaW * Math.min(pct, 1);

        // Colour: green < 80%, amber 80-100%, red > 100%
        const barColor: [number,number,number] =
          (typeof stdLimit !== 'number') ? C.navyMid
          : avg <= stdLimit * 0.8 ? C.emerald
          : avg <= stdLimit       ? C.amber
          : C.red;

        const rowY = y + idx * rowStep;

        // Page overflow guard — if this row would go past the page, stop (remaining params on next page would need extra logic but typically fit)
        if (rowY + barH > pageH - 20) return;

        // Label
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...C.black);
        doc.text(cp.label, margin, rowY + barH - 1.5);
        if (cp.unit) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(6);
          doc.setTextColor(...C.grey);
          doc.text(`(${cp.unit})`, margin, rowY + barH + 2.5);
        }

        // Bar background
        doc.setFillColor(...C.greyBg);
        doc.rect(barX, rowY, barAreaW, barH, 'F');

        // Bar fill
        if (barFillW > 0) {
          doc.setFillColor(...barColor);
          doc.rect(barX, rowY, barFillW, barH, 'F');
        }

        // Limit line (vertical dashed line at 100%)
        if (typeof stdLimit === 'number' && stdLimit > 0) {
          doc.setDrawColor(...C.grey);
          doc.setLineWidth(0.3);
          // Simple solid line (jsPDF has no native dashed line shortcut)
          doc.line(barX + barAreaW, rowY, barX + barAreaW, rowY + barH);
        }

        // Measured value text
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...barColor);
        const avgTxt = cp.key === 'ph' ? avg.toFixed(2) : avg.toFixed(1);
        doc.text(avgTxt, barX + barAreaW + 2, rowY + barH - 1.5);

        // Limit text
        if (typeof stdLimit === 'number') {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(6);
          doc.setTextColor(...C.grey);
          const limTxt = `/ ${stdLimit}`;
          doc.text(limTxt, barX + barAreaW + 2, rowY + barH + 2.5);
        }
      });

      // Legend
      const legendY = y + chartParams.length * rowStep + 6;
      if (legendY < pageH - 20) {
        const legendItems: Array<{ color: [number,number,number]; label: string }> = [
          { color: C.emerald,  label: 'Within limit (< 80%)' },
          { color: C.amber,    label: 'Approaching limit (80–100%)' },
          { color: C.red,      label: 'Exceeds limit (> 100%)' },
          { color: C.navyMid,  label: 'No limit set' },
        ];
        let lx = margin;
        legendItems.forEach(li => {
          doc.setFillColor(...li.color);
          doc.rect(lx, legendY, 5, 3, 'F');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(6.5);
          doc.setTextColor(...C.grey);
          doc.text(li.label, lx + 7, legendY + 2.5);
          lx += 46;
        });

        // Note about limit line
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        doc.setTextColor(...C.grey);
        doc.text('Vertical line = discharge limit.  Bar length = measured average relative to limit.  Values are averages across all samples in this report.', margin, legendY + 9);
      }

      y = legendY + 16;
    }
  }

  // ── Authorised Signatory Block ─────────────────────────────────────────────
  if (y > pageH - 55) {
    doc.addPage();
    doc.setFillColor(...C.white);
    doc.rect(0, 0, pageW, pageH, 'F');
    y = drawPageHeader(doc, pageW, margin, stdText, opts.reportNumber) + 4;
  }
  {
    const sigH = 40;
    doc.setFillColor(...C.greyBg); doc.setDrawColor(...C.greyLt);
    doc.rect(margin, y, cW, sigH, 'FD');

    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...C.navyMid);
    doc.text('DOCUMENT AUTHORISATION', margin + 3, y + 5);

    const sigColW = cW / 3;
    const sigLabels = [
      ['Prepared by:', opts.preparedBy || opts.signatoryName || 'Reynolds & Bauhm Ltd', opts.signatoryTitle || 'Technical Author'],
      ['Reviewed by:', '________________________________', 'Senior Engineer / QA'],
      ['Authorised by:', '________________________________', 'Regulatory Affairs Lead'],
    ];
    sigLabels.forEach(([label, name, role], i) => {
      const sx = margin + i * sigColW;
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(...C.grey);
      doc.text(label, sx + 3, y + 12);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...C.black);
      doc.text(name, sx + 3, y + 18);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(...C.grey);
      doc.text(role, sx + 3, y + 22.5);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...C.grey);
      doc.text('Signature: ____________________', sx + 3, y + 29);
      doc.text(`Date: ${i === 0 ? genDate : '________________'}`, sx + 3, y + 34.5);
    });
    y += sigH + 5;
  }

  // ── Regulatory Notice / Disclaimer ────────────────────────────────────────
  if (y > pageH - 30) {
    doc.addPage();
    doc.setFillColor(...C.white);
    doc.rect(0, 0, pageW, pageH, 'F');
    y = drawPageHeader(doc, pageW, margin, stdText, opts.reportNumber) + 4;
  }
  {
    const discText = `This report is prepared by Reynolds & Bauhm Ltd. ${stdNote} ` +
      `All analytical results must be validated by a UKAS/NATA/EPA-certified laboratory. ` +
      `Report Reference: ${opts.reportNumber}  ·  Regulatory Standard: ${stdLabel}  ·  Issue Date: ${genDate}`;
    const discLines = doc.splitTextToSize(discText, cW - 6);
    const discH = 8 + discLines.length * 3.8;
    doc.setFillColor(255, 251, 235); doc.setDrawColor(205, 164, 46);
    doc.roundedRect(margin, y, cW, discH, 2, 2, 'FD');
    doc.setFont('helvetica', 'bold');   doc.setFontSize(7.5); doc.setTextColor(146, 64, 14);
    doc.text('REGULATORY NOTICE', margin + 3, y + 5);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(92, 63, 10);
    doc.text(discLines, margin + 3, y + 9.5);
  }

  // ── Watermarks + Footers ────────────────────────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  stampAllPages(doc, pageW, pageH, opts.standard);
  drawFooters(doc, pageCount, pageW, pageH, margin, opts.reportNumber, stdLabel, stdNote);

  if (mode === 'preview') {
    const blob = doc.output('blob');
    return URL.createObjectURL(blob);
  }
  const safeRef = opts.reportNumber.replace(/[^a-zA-Z0-9_\-]/g, '_');
  doc.save(`${safeRef}_SampleTrendReport.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
//  REPORT 3 — Single-Sample Water Quality Report  (InfluentEntry / EffluentEntry)
// ═══════════════════════════════════════════════════════════════════════════════

interface TrendReportData {
  id: string;
  reportNumber: string;
  sampleName: string;
  customerName: string;
  customerCompany: string;
  standard: string;
  date: Date | string;
  preparerName: string;
  preparerCompany: string;
  siteName?: string;
  siteAddress?: string;
  permitRef?: string;
  siteJurisdiction?: string;
  params?: Record<string, number>;
  dosingConfig?: any[];
  result?: Record<string, any>;
}

export function generateTrendReportPDF(data: TrendReportData, mode: 'save' | 'preview' = 'save'): string | undefined {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 18;
  const cW = pageW - margin * 2;

  doc.setFillColor(...C.white);
  doc.rect(0, 0, pageW, pageH, 'F');

  const std = ALL_STANDARDS.find(s => s.code === data.standard) || null;
  const stdText  = std ? `${std.shortName} — ${std.country}` : (data.standard || 'UK EA');
  const stdLabel = std?.report.dischargeStandardLabel || stdText;
  const stdNote  = std?.report.complianceStatement || 'Early assessment — not for regulatory submission without professional review.';
  const stdLegal = std?.report.legalReference || '';
  const regBody  = data.siteJurisdiction || std?.regulatoryBody || stdText;
  const formattedDate = fmtDate(data.date);
  // Currency symbol matched to regulatory jurisdiction
  const currencySymbol =
    data.standard === 'AU_EPA' || data.standard === 'SA_WATER' ? 'A$' :
    data.standard === 'US_EPA' ? '$' :
    data.standard === 'EU_UWWTD' ? '€' : // €
    '£'; // £ — default UK

  // ─── Page 1 Header ──────────────────────────────────────────────────────
  let y = drawPageHeader(doc, pageW, margin, stdText, data.reportNumber);

  // Title
  doc.setFillColor(...C.greyBg);
  doc.rect(margin, y, cW, 18, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...C.navy);
  doc.text('WATER QUALITY ASSESSMENT REPORT', margin + 4, y + 7);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...C.grey);
  doc.text(data.sampleName || 'Sample Analysis', margin + 4, y + 13);
  y += 22;

  // Meta
  const metaRows: [string, string][] = [
    ['Reference:', data.reportNumber],
    ['Issue Date:', formattedDate],
    ['Prepared by:', `${data.preparerName}${data.preparerCompany ? ' — ' + data.preparerCompany : ''}`],
    ['Client:', `${data.customerName}${data.customerCompany ? ' — ' + data.customerCompany : ''}`],
    ['Discharge Standard:', stdLabel],
  ];
  if (data.siteName)     metaRows.push(['Discharge Site:', data.siteName]);
  if (data.siteAddress)  metaRows.push(['Site Address:', data.siteAddress]);
  if (data.permitRef)    metaRows.push(['Permit / Consent Ref:', data.permitRef]);
  if (regBody)           metaRows.push(['Regulatory Authority:', regBody]);
  if (stdLegal)          metaRows.push(['Legal Reference:', stdLegal]);

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    tableWidth: cW,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 2.5 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 55, textColor: C.grey },
      1: { textColor: C.black },
    },
    body: metaRows,
    willDrawCell: (data2: any) => {
      if (data2.section === 'body' && data2.column.index === 0 && data2.row.index % 2 === 0) {
        doc.setFillColor(...C.greyBg);
        doc.rect(data2.cell.x, data2.cell.y, cW, data2.row.height, 'F');
      }
    },
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

  // Regulatory banner
  if (std) { y = drawRegulatoryBanner(doc, y, margin, cW, std); }

  // ─── Water Parameters Table ─────────────────────────────────────────────
  if (data.params && Object.keys(data.params).length > 0) {
    y = sectionHead(doc, y, margin, pageW, 'Water Quality Parameters');

    const paramFlatRows = Object.entries(data.params)
      .filter(([, v]) => typeof v === 'number')
      .map(([key, val]) => {
        const stdLimit = std ? (std.limits as any)[key.toLowerCase()] : undefined;
        const status = typeof stdLimit === 'number'
          ? ((val as number) <= stdLimit ? 'PASS' : 'FAIL')
          : '—';
        return [key.toUpperCase().replace('OILGREASE', 'O&G').replace('NH3N', 'NH3-N'), (val as number).toFixed(2), getUnit(key), stdLimit != null ? String(stdLimit) : '—', status];
      });
    const paramRows = buildGroupedRows(paramFlatRows, 0);

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      tableWidth: cW,
      head: [['Parameter', 'Measured Value', 'Unit', 'Std Limit', 'Status']],
      body: paramRows,
      headStyles: { fillColor: C.navyMid, textColor: C.white, fontStyle: 'bold', fontSize: 9, cellPadding: 3 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: {
        0: { cellWidth: 40, fontStyle: 'bold' },
        1: { cellWidth: 35, halign: 'right' as const },
        2: { cellWidth: 30 },
        3: { cellWidth: 30, halign: 'right' as const },
        4: { cellWidth: 25, halign: 'center' as const, fontStyle: 'bold' },
      },
      alternateRowStyles: { fillColor: C.greyBg },
      theme: 'grid',
      didParseCell: (hookData: any) => {
        // Skip group header rows (they are objects not strings)
        if (typeof hookData.cell.raw === 'object') return;
        if (hookData.column.index === 4 && hookData.section === 'body') {
          if (hookData.cell.raw === 'PASS') { hookData.cell.styles.textColor = C.emerald; hookData.cell.styles.fillColor = C.emeraldBg; }
          else if (hookData.cell.raw === 'FAIL') { hookData.cell.styles.textColor = C.red; hookData.cell.styles.fillColor = C.redBg; }
        }
      },
    });
    y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;
  }

  // ─── Dosing Configuration ───────────────────────────────────────────────
  if (data.dosingConfig && data.dosingConfig.length > 0) {
    if (y > pageH - 80) {
      doc.addPage();
      doc.setFillColor(...C.white);
      doc.rect(0, 0, pageW, pageH, 'F');
      y = drawPageHeader(doc, pageW, margin, stdText, data.reportNumber) + 4;
    }
    y = sectionHead(doc, y, margin, pageW, 'Dosing Configuration');

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      tableWidth: cW,
      head: [['Chemical', 'Status', 'Dosage', 'Unit', 'Cost/Day']],
      body: data.dosingConfig.map((d: any) => [
        d?.name || '—',
        d?.enabled ? 'Active' : 'Inactive',
        d?.dosage !== undefined ? (typeof d.dosage === 'number' ? d.dosage.toFixed(2) : d.dosage) : '—',
        'mg/L',
        d?.cost !== undefined ? currencySymbol + (typeof d.cost === 'number' ? d.cost.toFixed(2) : d.cost) : '—',
      ]),
      headStyles: { fillColor: C.gold, textColor: C.navy, fontStyle: 'bold', fontSize: 9, cellPadding: 3 },
      styles: { fontSize: 9, cellPadding: 3 },
      alternateRowStyles: { fillColor: C.greyBg },
      theme: 'grid',
    });
    y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;
  }

  // ─── Compliance Summary ─────────────────────────────────────────────────
  if (data.result) {
    const summary = data.result.complianceSummary;
    const overall = summary?.overall || 'Pending Review';
    const compliant = overall === 'Compliant' || overall === 'Pass';

    if (y > pageH - 50) {
      doc.addPage();
      doc.setFillColor(...C.white);
      doc.rect(0, 0, pageW, pageH, 'F');
      y = drawPageHeader(doc, pageW, margin, stdText, data.reportNumber) + 4;
    }
    y = sectionHead(doc, y, margin, pageW, 'Compliance Summary');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...(compliant ? C.emerald : C.red));
    const statusText = compliant
      ? `✓  COMPLIANT — Meets ${stdLabel} discharge requirements`
      : `✗  REVIEW REQUIRED — Parameters exceed ${stdLabel} limits`;
    const stLines = doc.splitTextToSize(statusText, cW - 8);
    const stH = 8 + stLines.length * 5;
    doc.setFillColor(...(compliant ? [236, 253, 245] : [254, 242, 242]) as [number,number,number]);
    doc.setDrawColor(...(compliant ? C.emerald : C.red));
    doc.roundedRect(margin, y, cW, stH, 2, 2, 'FD');
    doc.text(stLines, pageW / 2, y + 6, { align: 'center' });
    y += stH + 4;
  }

  // ─── Watermarks + Footers ───────────────────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  stampAllPages(doc, pageW, pageH, data.standard);
  drawFooters(doc, pageCount, pageW, pageH, margin, data.reportNumber, stdLabel, stdNote);

  if (mode === 'preview') {
    const blob = doc.output('blob');
    return URL.createObjectURL(blob);
  }
  const safeName = (data.sampleName || data.reportNumber).replace(/[^a-zA-Z0-9_\-]/g, '_').slice(0, 40);
  doc.save(`${data.reportNumber}_WaterQualityReport_${safeName}.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
//  REPORT 4 — Process Train & Simulation Report  (replaces window.print in
//              PrintReportTabDark inside ResultsTabs)
// ═══════════════════════════════════════════════════════════════════════════════

export interface ProcessTrainReportOptions {
  reportNumber?: string;
  customerName?: string;
  siteName?: string;
  permitRef?: string;
  regulatoryAuthority?: string;
  preparedBy?: string;
  standard?: string;
}

export function generateProcessTrainReportPDF(
  result: AssessmentResult,
  blended: BlendedParameters,
  limits: DischargeLimits,
  samples: SavedSample[],
  opts: ProcessTrainReportOptions = {},
) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 18;
  const cW = pageW - margin * 2;

  doc.setFillColor(...C.white);
  doc.rect(0, 0, pageW, pageH, 'F');

  const std = opts.standard ? ALL_STANDARDS.find(s => s.code === opts.standard) || null : null;
  const stdText  = std ? `${std.shortName} — ${std.country}` : (opts.standard || 'UK EA');
  const stdLabel = std?.report.dischargeStandardLabel || stdText;
  const stdNote  = std?.report.complianceStatement || 'Early assessment — not for regulatory submission without professional review.';

  const now = new Date();
  const genDate = fmtDate(now);
  const safeN = (n: any) => (typeof n === 'number' && !isNaN(n) ? n : 0);

  const reportRef = opts.reportNumber || `RB-PTR-${now.toISOString().slice(0, 10).replace(/-/g, '')}-${now.toTimeString().slice(0, 5).replace(':', '')}`;

  // ─── PAGE 1 ──────────────────────────────────────────────────────────────
  let y = drawPageHeader(doc, pageW, margin, stdText, reportRef);

  // Title
  doc.setFillColor(...C.greyBg);
  doc.rect(margin, y, cW, 18, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...C.navy);
  doc.text('PROCESS SELECTION & EQUIPMENT SIZING REPORT', margin + 4, y + 7);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...C.grey);
  doc.text('Wastewater Treatment Assessment — Reynolds & Bauhm Ltd', margin + 4, y + 13);
  y += 22;

  // Meta
  const metaRows: [string, string][] = [
    ['Report Reference:', reportRef],
    ['Issue Date:', genDate],
    ['Prepared by:', opts.preparedBy || 'Reynolds & Bauhm Ltd'],
    ['Client:', opts.customerName || '—'],
    ['Discharge Standard:', stdLabel],
    ['Treatability Class:', result.treatabilityClassification || '—'],
    ['Treatability Index:', String(result.overallTreatabilityIndex ?? '—')],
  ];
  if (opts.siteName)            metaRows.push(['Discharge Site:', opts.siteName]);
  if (opts.permitRef)           metaRows.push(['Permit / Consent Ref:', opts.permitRef]);
  if (opts.regulatoryAuthority) metaRows.push(['Regulatory Authority:', opts.regulatoryAuthority]);
  if (std?.report.legalReference) metaRows.push(['Legal Reference:', std.report.legalReference]);

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    tableWidth: cW,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 2.5 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 55, textColor: C.grey },
      1: { textColor: C.black },
    },
    body: metaRows,
    willDrawCell: (d: any) => {
      if (d.section === 'body' && d.column.index === 0 && d.row.index % 2 === 0) {
        doc.setFillColor(...C.greyBg);
        doc.rect(d.cell.x, d.cell.y, cW, d.row.height, 'F');
      }
    },
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

  // Regulatory banner
  if (std) { y = drawRegulatoryBanner(doc, y, margin, cW, std); }

  // ── Input Water Quality ────────────────────────────────────────────────────
  y = sectionHead(doc, y, margin, pageW, 'Influent Water Quality — Design Basis');

  const bp = blended?.params || {};
  const inputRows = [
    ['Flow Rate', `${safeN(bp.flowRate).toFixed(1)} m³/day`, 'BOD5', `${safeN(bp.bod5).toFixed(0)} mg/L`],
    ['COD', `${safeN(bp.cod).toFixed(0)} mg/L`, 'TSS', `${safeN(bp.tss).toFixed(0)} mg/L`],
    ['pH', safeN(bp.ph).toFixed(2), 'TDS', `${safeN(bp.tds).toFixed(0)} mg/L`],
    ['TKN / NH3-N', `${safeN(bp.tkn).toFixed(0)} mg/L`, 'Total P', `${safeN(bp.totalPhosphorus).toFixed(0)} mg/L`],
    ['Oil & Grease', `${safeN(bp.oilGrease).toFixed(0)} mg/L`, 'Temperature', `${safeN(bp.temperature).toFixed(1)} °C`],
    ['Conductivity', `${safeN(bp.conductivity).toFixed(0)} µS/cm`, 'BOD/COD Ratio', safeN(bp.cod) > 0 ? (safeN(bp.bod5) / safeN(bp.cod)).toFixed(2) : '—'],
  ];

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    tableWidth: cW,
    head: [['Parameter', 'Value', 'Parameter', 'Value']],
    body: inputRows,
    headStyles: { fillColor: C.navyMid, textColor: C.white, fontStyle: 'bold', fontSize: 8, cellPadding: 3 },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 40, fontStyle: 'bold', textColor: C.grey },
      1: { cellWidth: 35, fontStyle: 'bold', textColor: C.navy },
      2: { cellWidth: 40, fontStyle: 'bold', textColor: C.grey },
      3: { cellWidth: 'auto', fontStyle: 'bold', textColor: C.navy },
    },
    alternateRowStyles: { fillColor: C.greyBg },
    theme: 'grid',
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

  // ── Discharge Limits ───────────────────────────────────────────────────────
  y = sectionHead(doc, y, margin, pageW, 'Discharge Limits Applied');

  const limitRows2 = [
    ['BOD5', `${limits.bod5} mg/L`, 'COD', `${limits.cod} mg/L`],
    ['TSS', `${limits.tss} mg/L`, 'Oil & Grease', `${limits.oilGrease} mg/L`],
    ['Total Nitrogen', `${limits.totalNitrogen} mg/L`, 'Total Phosphorus', `${limits.totalPhosphorus} mg/L`],
    [`pH`, `${limits.phMin} – ${limits.phMax}`, 'Temperature', `${limits.temperature} °C max`],
    ['Conductivity', `${limits.conductivity} µS/cm`, 'TDS', `${(limits as any).tds ?? '—'} mg/L`],
  ];

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    tableWidth: cW,
    head: [['Parameter', 'Limit', 'Parameter', 'Limit']],
    body: limitRows2,
    headStyles: { fillColor: C.navyMid, textColor: C.white, fontStyle: 'bold', fontSize: 8, cellPadding: 3 },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 40, fontStyle: 'bold' },
      1: { cellWidth: 35, fontStyle: 'bold', textColor: C.navyMid, halign: 'right' as const },
      2: { cellWidth: 40, fontStyle: 'bold' },
      3: { cellWidth: 'auto', fontStyle: 'bold', textColor: C.navyMid, halign: 'right' as const },
    },
    alternateRowStyles: { fillColor: C.greyBg },
    theme: 'grid',
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

  // ─── PAGE 2 — Process Train ────────────────────────────────────────────────
  if (result.simulation?.stages?.length) {
    doc.addPage();
    doc.setFillColor(...C.white);
    doc.rect(0, 0, pageW, pageH, 'F');
    y = drawPageHeader(doc, pageW, margin, stdText, reportRef) + 4;

    y = sectionHead(doc, y, margin, pageW, `Proposed Treatment Train — ${result.simulation.stages.length} Stages`);

    const stageRows = result.simulation.stages.map(stage => {
      const compliant =
        safeN(stage.tss) <= limits.tss &&
        safeN(stage.bod5) <= limits.bod5 &&
        safeN(stage.cod) <= limits.cod &&
        safeN(stage.ph) >= limits.phMin &&
        safeN(stage.ph) <= limits.phMax;
      const tanks = (stage.tanks || []).map((t: any) => `${t.name}: ${t.volumeM3} m³`).join('; ');
      const chems = (stage.chemicals || []).map((c: any) => `${c.name} ${c.dose} ${c.unit}`).join(', ');
      return [
        String(stage.stageNumber),
        stage.stageName,
        (stage.equipment || []).map((e: any) => e.name).join(', '),
        `pH ${safeN(stage.ph).toFixed(1)} | BOD ${safeN(stage.bod5).toFixed(0)} | COD ${safeN(stage.cod).toFixed(0)} | TSS ${safeN(stage.tss).toFixed(0)} mg/L`,
        tanks || '—',
        chems || '—',
        compliant ? 'COMPLIANT' : 'REVIEW',
      ];
    });

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      tableWidth: cW,
      head: [['#', 'Stage', 'Equipment', 'Effluent Quality', 'Tanks', 'Chemicals', 'Status']],
      body: stageRows,
      headStyles: { fillColor: C.navyMid, textColor: C.white, fontStyle: 'bold', fontSize: 7.5, cellPadding: 2.5 },
      styles: { fontSize: 7.5, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 8, halign: 'center' as const },
        1: { cellWidth: 28, fontStyle: 'bold' },
        2: { cellWidth: 28 },
        3: { cellWidth: 42 },
        4: { cellWidth: 28 },
        5: { cellWidth: 'auto' },
        6: { cellWidth: 18, halign: 'center' as const, fontStyle: 'bold' },
      },
      alternateRowStyles: { fillColor: C.greyBg },
      theme: 'grid',
      didParseCell: (d: any) => {
        if (d.column.index === 6) {
          d.cell.styles.textColor = d.cell.raw === 'COMPLIANT' ? C.emerald : C.amber;
        }
      },
    });
    y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;
  }

  // ── Sample History ─────────────────────────────────────────────────────────
  const sortedSamples = [...samples].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  if (sortedSamples.length > 0) {
    if (y > pageH - 80) {
      doc.addPage();
      doc.setFillColor(...C.white);
      doc.rect(0, 0, pageW, pageH, 'F');
      y = drawPageHeader(doc, pageW, margin, stdText, reportRef) + 4;
    }
    y = sectionHead(doc, y, margin, pageW, `Sample History — ${sortedSamples.length} Samples`);

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      tableWidth: cW,
      head: [['Date', 'Sample', 'BOD5', 'COD', 'TSS', 'pH', 'TDS', 'TKN']],
      body: sortedSamples.map(s => [
        fmtDate(s.date),
        s.name,
        safeN(s.params?.bod5).toFixed(0),
        safeN(s.params?.cod).toFixed(0),
        safeN(s.params?.tss).toFixed(0),
        safeN(s.params?.ph).toFixed(2),
        safeN(s.params?.tds).toFixed(0),
        safeN(s.params?.tkn).toFixed(0),
      ]),
      headStyles: { fillColor: C.navyMid, textColor: C.white, fontStyle: 'bold', fontSize: 8, cellPadding: 3 },
      styles: { fontSize: 8.5, cellPadding: 2.5 },
      columnStyles: {
        0: { cellWidth: 32 },
        1: { cellWidth: 36 },
        2: { cellWidth: 18, halign: 'right' as const },
        3: { cellWidth: 18, halign: 'right' as const },
        4: { cellWidth: 18, halign: 'right' as const },
        5: { cellWidth: 18, halign: 'right' as const },
        6: { cellWidth: 18, halign: 'right' as const },
        7: { cellWidth: 'auto', halign: 'right' as const },
      },
      alternateRowStyles: { fillColor: C.greyBg },
      theme: 'grid',
    });
    y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;
  }

  // ── Disclaimer ────────────────────────────────────────────────────────────
  if (y > pageH - 40) {
    doc.addPage();
    doc.setFillColor(...C.white);
    doc.rect(0, 0, pageW, pageH, 'F');
    y = drawPageHeader(doc, pageW, margin, stdText, reportRef) + 4;
  }
  {
    const disc2 = `This report is an early-stage assessment prepared by Reynolds & Bauhm Ltd. ${stdNote} ` +
      `All calculations are indicative and must be verified by detailed engineering design. ` +
      `Reynolds & Bauhm Ltd accepts no liability for decisions made solely on the basis of this report.`;
    const disc2Lines = doc.splitTextToSize(disc2, cW - 6);
    const disc2H = 8 + disc2Lines.length * 3.6 + 3; // dynamic height
    doc.setFillColor(255, 251, 235);
    doc.setDrawColor(205, 164, 46);
    doc.roundedRect(margin, y, cW, disc2H, 2, 2, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(146, 64, 14);
    doc.text('IMPORTANT — ENGINEERING NOTICE', margin + 3, y + 5.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(92, 63, 10);
    doc.text(disc2Lines, margin + 3, y + 10);
    y += disc2H;
  }

  // Watermarks + Footers
  const pageCount = doc.getNumberOfPages();
  stampAllPages(doc, pageW, pageH, opts.standard);
  drawFooters(doc, pageCount, pageW, pageH, margin, reportRef, stdLabel, stdNote);

  doc.save(`${reportRef}_ProcessTrainReport.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
//  REPORT 5 — DAF Effluent Quality Report
// ═══════════════════════════════════════════════════════════════════════════════

export interface DAFReportOptions {
  reportNumber: string;
  customerName?: string;
  siteName?: string;
  siteAddress?: string;
  permitRef?: string;
  regulatoryAuthority?: string;
  preparedBy?: string;
  standard?: string;
  influentSampleRef?: string;
  createdAt?: Date | string;
}

/** Minimal DAF input/result shapes — mirrors DAFSpecPage exports */
export interface DAFReportParams {
  flowRate: number;
  tss: number; og: number; bod: number; cod: number;
  ph: number; temp: number; alkalinity: number;
  turbidity?: number; conductivity?: number; hardness?: number;
  nh3n?: number; tp?: number;
  length?: number; width?: number; depth?: number;
  airPressure?: number; recycleRate?: number;
  saturatorPressure?: number;
}

export interface DAFReportResult {
  tssRemoval: number; ogRemoval: number; bodRemoval: number; codRemoval: number;
  effluentTSS: number; effluentOG: number; effluentBOD: number; effluentCOD: number; effluentPH: number;
  hlr: number; slr?: number; actualRetention?: number; airToSolids: number;
  sludgeTotal?: number; sludgeVolume?: number;
  power?: number;
  status: string; statusDetail?: string;
  designNotes?: string[];
}

/** Strip / replace characters outside Windows-1252 (WinAnsi) so jsPDF
 *  Helvetica renders them cleanly. Non-Latin-1 glyphs cause jsPDF to emit
 *  spaced-out, garbled text for the rest of the line. */
function sanitizeForPDF(text: string): string {
  // Named explicit replacements for common engineering / UI symbols
  let s = text;
  // Warning / info emoji  (multi-codepoint sequences first)
  s = s.replace(/⚠️/g, '[!]');   // ⚠️
  s = s.replace(/⚠/g,       '[!]');   // ⚠
  s = s.replace(/❗️/g, '[!]');   // ❗️
  s = s.replace(/❗/g,       '[!]');   // ❗
  s = s.replace(/⁉️/g, '[!]');   // ‼️
  s = s.replace(/ℹ️/g, '[i]');   // ℹ️
  s = s.replace(/ℹ/g,       '[i]');   // ℹ
  s = s.replace(/✓/g,       '[OK]');  // ✓
  s = s.replace(/✔/g,       '[OK]');  // ✔
  s = s.replace(/✗/g,       '[X]');   // ✗
  s = s.replace(/✘/g,       '[X]');   // ✘
  // Superscripts / subscripts
  s = s.replace(/s⁻\xB9/g,  's^-1'); // s⁻¹
  s = s.replace(/⁻/g,       '-');    // ⁻
  s = s.replace(/¹/g,       '1');    // ¹  (U+00B9 — in Latin-1 but jsPDF maps oddly)
  // ² and ³ ARE in WinAnsi (0xB2, 0xB3) — keep them
  // Punctuation / math
  s = s.replace(/[–—]/g, '-');   // en-dash, em-dash
  s = s.replace(/[“”]/g, '"');   // curly double quotes
  s = s.replace(/[‘’]/g, "'");   // curly single quotes
  s = s.replace(/×/g,         'x');   // ×  (multiplication — in Latin-1 but use 'x' for clarity)
  s = s.replace(/≤/g,         '<=');  // ≤
  s = s.replace(/≥/g,         '>=');  // ≥
  // Catch-all: anything still above U+00FF is outside Latin-1 → replace with ?
  s = s.replace(/[^\x00-\xFF]/g, '?');
  return s;
}

export function generateDAFReportPDF(
  params: DAFReportParams,
  result: DAFReportResult,
  opts: DAFReportOptions,
  mode: 'save' | 'preview' = 'save',
): string | undefined {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 18;
  const cW = pageW - margin * 2;

  doc.setFillColor(...C.white);
  doc.rect(0, 0, pageW, pageH, 'F');

  const std = opts.standard ? ALL_STANDARDS.find(s => s.code === opts.standard) || null : null;
  const stdText  = std ? `${std.shortName} — ${std.country}` : (opts.standard || 'UK EA');
  const stdLabel = std?.report.dischargeStandardLabel || stdText;
  const stdNote  = std?.report.complianceStatement || 'Early assessment — not for regulatory submission without professional review.';
  const genDate  = fmtDate(opts.createdAt || new Date());
  const safeN    = (n: any) => (typeof n === 'number' && !isNaN(n) ? n : 0);

  // ─── Page 1 Header ──────────────────────────────────────────────────────
  let y = drawPageHeader(doc, pageW, margin, stdText, opts.reportNumber);

  // Title block
  doc.setFillColor(...C.greyBg);
  doc.rect(margin, y, cW, 18, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...C.navy);
  doc.text('DAF EFFLUENT QUALITY REPORT', margin + 4, y + 7);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...C.grey);
  doc.text('Dissolved Air Flotation — Performance & Compliance Assessment', margin + 4, y + 13);
  y += 22;

  // Sanitize all user-supplied strings before embedding in PDF
  // (customer names, site addresses etc. can contain emoji from copy-paste)
  const sp = (s: string | undefined) => s ? sanitizeForPDF(s) : '';

  // Document control / meta table
  const metaRows: [string, string][] = [
    ['Report Reference:', opts.reportNumber],
    ['Issue Date:', genDate],
    ['Prepared by:', sp(opts.preparedBy) || 'Reynolds & Bauhm Ltd'],
    ['Discharge Standard:', stdLabel],
  ];
  if (opts.customerName)        metaRows.push(['Client:', sp(opts.customerName)]);
  if (opts.siteName)            metaRows.push(['Discharge Site:', sp(opts.siteName)]);
  if (opts.siteAddress)         metaRows.push(['Site Address:', sp(opts.siteAddress)]);
  if (opts.permitRef)           metaRows.push(['Permit / Consent Ref:', sp(opts.permitRef)]);
  if (opts.regulatoryAuthority) metaRows.push(['Regulatory Authority:', sp(opts.regulatoryAuthority)]);
  if (opts.influentSampleRef)   metaRows.push(['Influent Source Sample:', sp(opts.influentSampleRef)]);
  if (std?.report.legalReference) metaRows.push(['Legal Reference:', std.report.legalReference]);

  autoTable(doc, {
    startY: y, margin: { left: margin, right: margin }, tableWidth: cW,
    theme: 'plain', styles: { fontSize: 9, cellPadding: 2.5 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55, textColor: C.grey }, 1: { textColor: C.black } },
    body: metaRows,
    willDrawCell: (d: any) => {
      if (d.section === 'body' && d.column.index === 0 && d.row.index % 2 === 0) {
        doc.setFillColor(...C.greyBg);
        doc.rect(d.cell.x, d.cell.y, cW, d.row.height, 'F');
      }
    },
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

  // Regulatory banner
  if (std) { y = drawRegulatoryBanner(doc, y, margin, cW, std); }

  // ── DAF Performance Overview banner ──────────────────────────────────────
  const compliant = result.status === 'optimal' || result.status === 'acceptable';
  const statusLabel = result.status.charAt(0).toUpperCase() + result.status.slice(1);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...(compliant ? C.emerald : C.red));
  const dafStatusText = sanitizeForPDF(compliant
    ? `[OK]  DAF STATUS: ${statusLabel.toUpperCase()} - System operating within design parameters`
    : `[!]  DAF STATUS: ${statusLabel.toUpperCase()} - ${result.statusDetail || 'Review design parameters'}`);
  const dafStLines = doc.splitTextToSize(dafStatusText, cW - 8);
  const dafStH = 8 + dafStLines.length * 5;
  doc.setFillColor(...(compliant ? [236, 253, 245] : [254, 242, 242]) as [number,number,number]);
  doc.setDrawColor(...(compliant ? C.emerald : C.red));
  doc.roundedRect(margin, y, cW, dafStH, 2, 2, 'FD');
  doc.text(dafStLines, pageW / 2, y + 6, { align: 'center', maxWidth: cW - 8 });
  y += dafStH + 4;

  // ── Influent Water Quality ────────────────────────────────────────────────
  y = sectionHead(doc, y, margin, pageW, 'Influent Water Quality — Design Basis');

  autoTable(doc, {
    startY: y, margin: { left: margin, right: margin }, tableWidth: cW,
    head: [['Parameter', 'Value', 'Unit', 'Parameter', 'Value', 'Unit']],
    body: [
      ['Flow Rate', safeN(params.flowRate).toFixed(1), 'm³/day',  'BOD5', safeN(params.bod).toFixed(0), 'mg/L'],
      ['TSS',  safeN(params.tss).toFixed(0), 'mg/L', 'COD', safeN(params.cod).toFixed(0), 'mg/L'],
      ['O&G',  safeN(params.og).toFixed(0), 'mg/L',  'pH', safeN(params.ph).toFixed(2), '—'],
      ['Temp', safeN(params.temp).toFixed(1), '°C',  'Alkalinity', safeN(params.alkalinity).toFixed(0), 'mg/L CaCO3'],
      ['NH3-N', safeN(params.nh3n).toFixed(1), 'mg/L', 'Total P', safeN(params.tp).toFixed(1), 'mg/L'],
    ],
    headStyles: { fillColor: C.navyMid, textColor: C.white, fontStyle: 'bold', fontSize: 8, cellPadding: 2.5 },
    styles: { fontSize: 8.5, cellPadding: 2.5 },
    columnStyles: {
      0: { cellWidth: 26, fontStyle: 'bold', textColor: C.grey },
      1: { cellWidth: 22, halign: 'right' as const, fontStyle: 'bold', textColor: C.navy },
      2: { cellWidth: 20 },
      3: { cellWidth: 26, fontStyle: 'bold', textColor: C.grey },
      4: { cellWidth: 22, halign: 'right' as const, fontStyle: 'bold', textColor: C.navy },
      5: { cellWidth: 'auto' },
    },
    alternateRowStyles: { fillColor: C.greyBg }, theme: 'grid',
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

  // ── DAF Performance Results ───────────────────────────────────────────────
  y = sectionHead(doc, y, margin, pageW, 'DAF Performance — Removal & Effluent Quality');

  // Build full discharge-gate compliance table (all parameters checked against standard)
  const chk = (val: number, limit: number | undefined, invert = false): 'PASS' | 'FAIL' | '—' => {
    if (!std || limit == null) return '—';
    const ok = invert ? val >= limit : val <= limit;
    return ok ? 'PASS' : 'FAIL';
  };
  const perfRows: string[][] = [
    ['TSS',          `${safeN(result.tssRemoval * 100).toFixed(1)}%`,   `${safeN(result.effluentTSS).toFixed(1)} mg/L`,
      std ? `<= ${std.limits.tss} mg/L` : '—',    chk(safeN(result.effluentTSS), std?.limits.tss)],
    ['O&G',          `${safeN(result.ogRemoval * 100).toFixed(1)}%`,    `${safeN(result.effluentOG).toFixed(1)} mg/L`,
      std ? `<= ${std.limits.oilGrease} mg/L` : '—', chk(safeN(result.effluentOG), std?.limits.oilGrease)],
    ['BOD5',         `${safeN(result.bodRemoval * 100).toFixed(1)}%`,   `${safeN(result.effluentBOD).toFixed(1)} mg/L`,
      std ? `<= ${std.limits.bod5} mg/L` : '—',   chk(safeN(result.effluentBOD), std?.limits.bod5)],
    ['COD',          `${safeN(result.codRemoval * 100).toFixed(1)}%`,   `${safeN(result.effluentCOD).toFixed(1)} mg/L`,
      std ? `<= ${std.limits.cod} mg/L` : '—',    chk(safeN(result.effluentCOD), std?.limits.cod)],
    ['pH',           '—',                                                safeN(result.effluentPH).toFixed(2),
      std ? `${std.limits.phMin}–${std.limits.phMax}` : '—',
      std ? (safeN(result.effluentPH) >= std.limits.phMin && safeN(result.effluentPH) <= std.limits.phMax ? 'PASS' : 'FAIL') : '—'],
  ];
  // Add NH3-N gate if influent has NH3-N data
  if (params.nh3n != null && safeN(params.nh3n) > 0) {
    // DAF has ~5% NH3-N removal (stripping) — apply minimal reduction
    const efflNH3 = safeN(params.nh3n) * 0.95;
    perfRows.push(['NH3-N', '~5%', `${efflNH3.toFixed(1)} mg/L`,
      std ? `<= ${std.limits.ammoniaN} mg/L` : '—', chk(efflNH3, std?.limits.ammoniaN)]);
  }
  // Add Total Phosphorus gate if data present
  if (params.tp != null && safeN(params.tp) > 0) {
    const efflTP = safeN(params.tp) * 0.60; // ~40% TP removal via coagulation/DAF
    perfRows.push(['Total P', '~40%', `${efflTP.toFixed(2)} mg/L`,
      std ? `<= ${std.limits.totalPhosphorus} mg/L` : '—', chk(efflTP, std?.limits.totalPhosphorus)]);
  }
  // Temperature gate
  if (safeN(params.temp) > 0) {
    perfRows.push(['Temperature', '—', `${safeN(params.temp).toFixed(1)} °C`,
      std ? `<= ${std.limits.temperature} °C` : '—', chk(safeN(params.temp), std?.limits.temperature)]);
  }
  // Conductivity gate
  if (params.conductivity != null && safeN(params.conductivity) > 0) {
    perfRows.push(['Conductivity', '—', `${safeN(params.conductivity).toFixed(0)} µS/cm`,
      std ? `<= ${std.limits.conductivity} µS/cm` : '—', chk(safeN(params.conductivity), std?.limits.conductivity)]);
  }

  autoTable(doc, {
    startY: y, margin: { left: margin, right: margin }, tableWidth: cW,
    head: [['Parameter', 'Removal %', 'Effluent Conc.', 'Discharge Limit', 'Compliance']],
    body: perfRows,
    headStyles: { fillColor: C.navyMid, textColor: C.white, fontStyle: 'bold', fontSize: 8, cellPadding: 3 },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 28, fontStyle: 'bold' },
      1: { cellWidth: 26, halign: 'right' as const },
      2: { cellWidth: 36, halign: 'right' as const, fontStyle: 'bold' },
      3: { cellWidth: 36, halign: 'center' as const },
      4: { cellWidth: 'auto', halign: 'center' as const, fontStyle: 'bold' },
    },
    alternateRowStyles: { fillColor: C.greyBg }, theme: 'grid',
    didParseCell: (d: any) => {
      if (d.column.index === 4) {
        if (d.cell.raw === 'PASS') d.cell.styles.textColor = C.emerald;
        else if (d.cell.raw === 'FAIL') d.cell.styles.textColor = C.red;
      }
    },
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

  // ── DAF Design Parameters ─────────────────────────────────────────────────
  if (y > pageH - 80) {
    doc.addPage();
    doc.setFillColor(...C.white);
    doc.rect(0, 0, pageW, pageH, 'F');
    y = drawPageHeader(doc, pageW, margin, stdText, opts.reportNumber) + 4;
  }
  y = sectionHead(doc, y, margin, pageW, 'DAF Design Parameters');

  autoTable(doc, {
    startY: y, margin: { left: margin, right: margin }, tableWidth: cW,
    head: [['Parameter', 'Value', 'Unit', 'Parameter', 'Value', 'Unit']],
    body: [
      ['HLR', safeN(result.hlr).toFixed(2), 'm³/m²/h',  'Air:Solids', safeN(result.airToSolids).toFixed(3), 'ratio'],
      ['SLR', safeN(result.slr ?? 0).toFixed(2), 'kg/m²/h', 'Retention Time', safeN(result.actualRetention ?? 0).toFixed(1), 'min'],
      ['Sludge Generated', safeN(result.sludgeTotal ?? 0).toFixed(1), 'kg/day', 'Sludge Volume', safeN(result.sludgeVolume ?? 0).toFixed(2), 'm³/day'],
      ['Total Power', safeN(result.power ?? 0).toFixed(2), 'kW', 'Recycle Rate', safeN(params.recycleRate ?? 0).toFixed(0), '%'],
    ],
    headStyles: { fillColor: C.gold, textColor: C.navy, fontStyle: 'bold', fontSize: 8, cellPadding: 2.5 },
    styles: { fontSize: 8.5, cellPadding: 2.5 },
    columnStyles: {
      0: { cellWidth: 34, fontStyle: 'bold', textColor: C.grey },
      1: { cellWidth: 24, halign: 'right' as const, fontStyle: 'bold', textColor: C.navy },
      2: { cellWidth: 18 },
      3: { cellWidth: 34, fontStyle: 'bold', textColor: C.grey },
      4: { cellWidth: 24, halign: 'right' as const, fontStyle: 'bold', textColor: C.navy },
      5: { cellWidth: 'auto' },
    },
    alternateRowStyles: { fillColor: C.greyBg }, theme: 'grid',
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

  // ── Design Notes / Warnings ───────────────────────────────────────────────
  if (result.designNotes && result.designNotes.length > 0) {
    // Pre-emptive page check: section heading + at least the first note must fit
    if (y > pageH - 50) {
      doc.addPage();
      doc.setFillColor(...C.white);
      doc.rect(0, 0, pageW, pageH, 'F');
      y = drawPageHeader(doc, pageW, margin, stdText, opts.reportNumber) + 8;
    }
    y = sectionHead(doc, y, margin, pageW, 'Design Notes & Warnings');
    result.designNotes.forEach((note, i) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...C.black);
      const lines = doc.splitTextToSize(`${i + 1}. ${sanitizeForPDF(note)}`, cW - 6);
      const noteH = lines.length * 4.2 + 2;
      // Check BEFORE rendering — prevents text bleeding past footer
      if (y + noteH > pageH - 25) {
        doc.addPage();
        doc.setFillColor(...C.white);
        doc.rect(0, 0, pageW, pageH, 'F');
        y = drawPageHeader(doc, pageW, margin, stdText, opts.reportNumber) + 8;
      }
      doc.text(lines, margin + 3, y);
      y += noteH;
    });
    y += 4;
  }

  // ── Disclaimer ────────────────────────────────────────────────────────────
  // Use generous threshold — stdNote can be 2–3 lines for some jurisdictions
  if (y > pageH - 52) {
    doc.addPage();
    doc.setFillColor(...C.white);
    doc.rect(0, 0, pageW, pageH, 'F');
    y = drawPageHeader(doc, pageW, margin, stdText, opts.reportNumber) + 4;
  }
  // Disclaimer — dynamic height so text never overflows the box
  const disc = sanitizeForPDF(
    `This DAF performance report is prepared by Reynolds & Bauhm Ltd for engineering assessment purposes. ${stdNote} ` +
    `Results must be validated by laboratory analysis and approved by a licensed engineer before regulatory submission. ` +
    `Reynolds & Bauhm Ltd accepts no liability for decisions made solely on the basis of this report.`
  );
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  const discLines = doc.splitTextToSize(disc, cW - 6);
  const discBoxH = 7 + discLines.length * 3.6 + 3;  // heading(5) + gap(2) + lines + bottom pad
  doc.setFillColor(255, 251, 235);
  doc.setDrawColor(205, 164, 46);
  doc.roundedRect(margin, y, cW, discBoxH, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(146, 64, 14);
  doc.text('IMPORTANT - REGULATORY NOTICE', margin + 3, y + 5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(92, 63, 10);
  doc.text(discLines, margin + 3, y + 9.5);

  // Watermarks + Footers
  const pageCount = doc.getNumberOfPages();
  stampAllPages(doc, pageW, pageH, opts.standard);
  drawFooters(doc, pageCount, pageW, pageH, margin, opts.reportNumber, stdLabel, stdNote);

  if (mode === 'preview') {
    return URL.createObjectURL(doc.output('blob'));
  }
  const safeRef = opts.reportNumber.replace(/[^a-zA-Z0-9_\-]/g, '_');
  doc.save(`${safeRef}_DAFEffluentReport.pdf`);
}

function getUnit(param: string): string {
  const units: Record<string, string> = {
    ph: 'pH units', cod: 'mg/L', bod: 'mg/L', tss: 'mg/L',
    tds: 'mg/L', nh3n: 'mg/L', tp: 'mg/L', oilgrease: 'mg/L',
    turbidity: 'NTU', temperature: '°C', conductivity: 'µS/cm',
    bod5: 'mg/L', no3n: 'mg/L', tn: 'mg/L', po4: 'mg/L',
    sulfide: 'mg/L', chloride: 'mg/L', hardness: 'mg/L CaCO3',
    alkalinity: 'mg/L CaCO3', mlss: 'mg/L', srt: 'days',
    do: 'mg/L', flow: 'm³/day', bod7: 'mg/L', toc: 'mg/L',
    ammonian: 'mg/L', totalnitrogen: 'mg/L', totalphosphorus: 'mg/L',
    oilgrease2: 'mg/L', lead: 'mg/L', chromium: 'mg/L',
  };
  return units[param.toLowerCase()] || 'mg/L';
}

// ═══════════════════════════════════════════════════════════════════════════════
//  REPORT 6 — Single Effluent Sample Discharge Compliance Report
//  Used on Customer page to preview individual effluent/DAF effluent samples
// ═══════════════════════════════════════════════════════════════════════════════

export interface EffluentSampleReportOptions {
  reportNumber?: string;
  /** Regulatory reference ID shown on the report, e.g. RB-AU/2026/EFF-94168 */
  sampleReferenceId?: string;
  sampleName: string;
  sampleDate?: string | Date;
  customerName?: string;
  siteName?: string;
  siteAddress?: string;
  permitRef?: string;
  regulatoryAuthority?: string;
  preparedBy?: string;
  standard?: string;
  linkedSampleId?: number | null;
  linkedInfluentRef?: string;
  notes?: string;
  isDafEffluent?: boolean;
}

export function generateEffluentSamplePDF(
  params: Record<string, number>,
  opts: EffluentSampleReportOptions,
  mode: 'save' | 'preview' = 'save',
): string | undefined {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 18;
  const cW = pageW - margin * 2;

  doc.setFillColor(...C.white);
  doc.rect(0, 0, pageW, pageH, 'F');

  const isSAWater = opts.standard === 'SA_WATER';
  const std = opts.standard ? ALL_STANDARDS.find(s => s.code === opts.standard) || null : null;
  const stdText  = isSAWater
    ? 'SA Water — Trade Waste Agreement (SA only)'
    : (std ? `${std.shortName} — ${std.country}` : (opts.standard || 'UK EA'));
  const stdLabel = isSAWater
    ? 'SA Water Trade Waste Agreement'
    : (std?.report.dischargeStandardLabel || stdText);
  const genDate  = fmtDate(new Date());
  const sampleDate = fmtDate(opts.sampleDate || new Date());

  const reportNum = opts.reportNumber || buildRegulatoryReportNumber(
    opts.isDafEffluent ? 'DAF' : 'WQ',
    opts.standard,
  );

  // ─── Header ──────────────────────────────────────────────────────────────
  const headerText = isSAWater ? 'SA WATER — TRADE WASTE AGREEMENT' : stdText;
  let y = drawPageHeader(doc, pageW, margin, headerText, reportNum);

  // Title block
  const titleType = isSAWater
    ? 'TRADE WASTE DISCHARGE REPORT'
    : opts.isDafEffluent
      ? 'EFFLUENT DISCHARGE COMPLIANCE REPORT — DAF TREATED WATER'
      : 'EFFLUENT DISCHARGE COMPLIANCE REPORT';
  const titleSubtitle = [opts.customerName, opts.siteName].filter(Boolean).join(' — ') || opts.sampleName;
  doc.setFillColor(...C.navyMid);
  doc.rect(margin, y, cW, 14, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...C.white);
  doc.text(titleType, margin + 4, y + 6);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...C.greyLt);
  doc.text(doc.splitTextToSize(titleSubtitle, cW - 8)[0], margin + 4, y + 12);
  y += 18;

  // SA Water — prominent "Prepared by Reynolds & Bauhm" attribution strip
  if (isSAWater) {
    const attrH = 10;
    doc.setFillColor(240, 253, 250);
    doc.setDrawColor(13, 148, 136);
    doc.setLineWidth(0.6);
    doc.roundedRect(margin, y, cW, attrH, 1.5, 1.5, 'FD');
    doc.setLineWidth(0.2);
    // Left label
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(15, 118, 110);
    doc.text('ASSESSMENT & REPORT PREPARED BY:', margin + 4, y + 4.5);
    // Right value
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(17, 94, 89);
    doc.text('Reynolds & Bauhm Ltd  —  All data collection, analysis and report generation performed by Reynolds & Bauhm Ltd', margin + 4, y + 8.5);
    y += attrH + 4;
  }

  // ── Meta table ─────────────────────────────────────────────────────────────
  // NOTE: jsPDF standard fonts (Helvetica) use WinAnsi encoding. Subscript/superscript
  // Unicode codepoints (e.g. subscript digits U+208X) and math symbols (U+2264 ≤) are
  // NOT in WinAnsi and produce garbage glyphs. Use ASCII-safe alternatives throughout.
  const refId = opts.sampleReferenceId || reportNum;
  const metaRows: [string, string][] = [
    ['Sample Reference ID:', refId],
    ['Report Reference:', reportNum],
    ['Report Date:', genDate],
    ['Sample Date:', sampleDate],
    ['Prepared by:', opts.preparedBy || 'Reynolds & Bauhm Ltd'],
    [isSAWater ? 'Trade Waste Agreement:' : 'Discharge Standard:', stdLabel],
  ];
  if (opts.customerName)  metaRows.push(['Client / Operator:', opts.customerName]);
  if (opts.siteName)      metaRows.push(['Discharge Site:', opts.siteName]);
  if (opts.siteAddress)   metaRows.push(['Site Address:', opts.siteAddress]);
  if (opts.permitRef)     metaRows.push([isSAWater ? 'Trade Waste Agreement Ref:' : 'Licence / Consent Ref:', opts.permitRef]);
  if (!isSAWater && opts.regulatoryAuthority) metaRows.push(['Regulatory Authority:', opts.regulatoryAuthority]);
  if (isSAWater) metaRows.push(['Sewerage Authority:', 'SA Water — Trade Waste Regulator under Sewerage Act 1929 (SA)']);
  if (opts.isDafEffluent) metaRows.push(['Treatment Process:', 'Dissolved Air Flotation (DAF) — post-flotation discharge']);
  if (opts.linkedInfluentRef || opts.linkedSampleId) {
    metaRows.push(['Linked Influent Record:', opts.linkedInfluentRef || `Sample ID: ${opts.linkedSampleId}`]);
  }
  if (!isSAWater && std?.report.legalReference) metaRows.push(['Applicable Legislation:', std.report.legalReference]);
  if (isSAWater) metaRows.push(['Applicable Legislation:', 'Sewerage Act 1929 (SA) — Trade Waste provisions']);

  autoTable(doc, {
    startY: y, margin: { left: margin, right: margin }, tableWidth: cW,
    theme: 'plain',
    styles: { fontSize: 8.5, cellPadding: 2.5, overflow: 'linebreak' as const },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 58, textColor: C.grey },
      1: { textColor: C.black, cellWidth: cW - 58 },
    },
    body: metaRows,
    didParseCell: (d: any) => {
      if (d.row.index === 0 && d.column.index === 1) {
        d.cell.styles.textColor = C.gold;
        d.cell.styles.fontStyle = 'bold';
        d.cell.styles.fontSize = 9;
      }
    },
    willDrawCell: (d: any) => {
      if (d.section === 'body' && d.column.index === 0 && d.row.index % 2 === 0) {
        doc.setFillColor(...C.greyBg);
        doc.rect(d.cell.x, d.cell.y, cW, d.row.height, 'F');
      }
    },
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 5;

  // Regulatory authority banner — SA Water uses its own trade waste banner below
  if (std && !isSAWater) {
    const bannerLine = `${std.name}  |  ${std.permitType}  |  ${std.report.certificationMark}`;
    const bLines = doc.splitTextToSize(bannerLine, cW - 8);
    const bH = 6 + bLines.length * 4.5;
    doc.setFillColor(240, 249, 255);
    doc.setDrawColor(147, 197, 253);
    doc.roundedRect(margin, y, cW, bH, 2, 2, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(30, 64, 175);
    doc.text(bLines, margin + 4, y + 5);
    y += bH + 5;
  }

  // SA Water banner (professional trade waste notice)
  if (isSAWater) {
    const sawBanner = 'SA Water — Sewerage Authority & Trade Waste Regulator (Sewerage Act 1929)  |  Trade Waste Agreement  |  South Australia';
    const bLines = doc.splitTextToSize(sawBanner, cW - 8);
    const bH = 6 + bLines.length * 4.5;
    doc.setFillColor(0, 82, 118, 0.05);  // teal tint
    doc.setFillColor(232, 247, 253);
    doc.setDrawColor(6, 182, 212, 0.4);
    doc.setDrawColor(56, 189, 248);
    doc.roundedRect(margin, y, cW, bH, 2, 2, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(3, 105, 161);
    doc.text(bLines, margin + 4, y + 5);
    y += bH + 5;
  }

  if (isSAWater) {
    // ── SA Water: Discharge vs Charge Tier Comparison ─────────────────────────
    y = sectionHead(doc, y, margin, pageW, 'Measured Discharge vs SA Water Charge Rate Thresholds');

    // SA Water charge rate data
    const sawRates = [
      { key: 'bod5',  aliases: ['bod5','bod'], label: 'BOD5', fullName: 'Biochemical Oxygen Demand', unit: 'mg/L',
        threshold: 1000, lowRate: 0.251, highRate: 0.378, triggerKg: 10000, tknCond: false },
      { key: 'tss',   aliases: ['tss','ss'],   label: 'SS',   fullName: 'Suspended Solids',          unit: 'mg/L',
        threshold: 500,  lowRate: 0.218, highRate: 0.315, triggerKg: 10000, tknCond: false },
      { key: 'tds',   aliases: ['tds'],        label: 'TDS',  fullName: 'Total Dissolved Solids',    unit: 'mg/L',
        threshold: null, lowRate: 0.126, highRate: 0.126, triggerKg: 20000, tknCond: false },
      { key: 'tn',    aliases: ['tn','tkn','totalnitrogen'], label: 'TKN', fullName: 'Total Kjeldahl Nitrogen', unit: 'mg/L',
        threshold: null, lowRate: 0.392, highRate: 0.392, triggerKg: null,  tknCond: true  },
      { key: 'totalphosphorus', aliases: ['totalphosphorus','tp','phosphorus'], label: 'TP', fullName: 'Total Phosphorus', unit: 'mg/L',
        threshold: null, lowRate: 1.908, highRate: 1.908, triggerKg: null,  tknCond: false },
    ];

    const sawRows: string[][] = [];
    for (const r of sawRates) {
      let measured: number | null = null;
      for (const alias of r.aliases) {
        const v = params[alias] ?? params[alias.toUpperCase()];
        if (typeof v === 'number' && v >= 0) { measured = v; break; }
      }
      if (measured === null) continue;

      const highTier = r.threshold !== null && measured > r.threshold;
      const rate = highTier ? r.highRate : r.lowRate;
      let rateNote = '';
      if (r.tknCond) {
        rateNote = measured > 650 ? 'Chargeable (avg > 650 mg/L)' : 'Not charged (avg <= 650 mg/L)';
      } else if (r.threshold !== null) {
        rateNote = highTier
          ? `HIGH RATE — exceeds ${r.threshold} mg/L threshold`
          : `Standard rate — within ${r.threshold} mg/L threshold`;
      } else {
        rateNote = 'Standard rate applies';
      }

      sawRows.push([
        r.label,
        r.fullName,
        `${measured.toFixed(measured < 10 ? 2 : 1)} ${r.unit}`,
        r.threshold !== null ? `${r.threshold} mg/L` : 'N/A',
        `$${rate.toFixed(3)}/kg`,
        rateNote,
      ]);
    }

    if (sawRows.length > 0) {
      autoTable(doc, {
        startY: y, margin: { left: margin, right: margin }, tableWidth: cW,
        head: [['Param', 'Parameter Name', 'Measured', 'High-Rate Threshold', 'Charge Rate', 'Rate Tier / Notes']],
        body: sawRows,
        headStyles: { fillColor: [3, 105, 161], textColor: C.white, fontStyle: 'bold', fontSize: 7.5, cellPadding: 2.5 },
        styles: { fontSize: 8, cellPadding: 2.5, overflow: 'linebreak' as const },
        columnStyles: {
          0: { cellWidth: 14, fontStyle: 'bold', halign: 'center' },
          1: { cellWidth: 46 },
          2: { cellWidth: 22, halign: 'right', fontStyle: 'bold' },
          3: { cellWidth: 26, halign: 'right' },
          4: { cellWidth: 20, halign: 'right', fontStyle: 'bold', textColor: C.amber },
          5: { cellWidth: cW - 128 },
        },
        didParseCell: (d: any) => {
          // Highlight high rate rows
          if (d.column.index === 5 && typeof d.cell.raw === 'string' && d.cell.raw.includes('HIGH RATE')) {
            d.cell.styles.textColor = C.red;
            d.cell.styles.fontStyle = 'bold';
          }
          if (d.column.index === 2) d.cell.styles.textColor = C.navyMid;
        },
        alternateRowStyles: { fillColor: C.greyBg },
        theme: 'grid',
      });
      y = ((doc as any).lastAutoTable?.finalY ?? y) + 5;
    }

    // SA Water charge info note
    const sawNote = 'NOTE: SA Water is the Sewerage Authority and Trade Waste Regulator for South Australia under the Sewerage Act 1929. ' +
      'Excess concentration charges apply when annual discharge exceeds 10,000 kg BOD or SS, 20,000 kg TDS, or 10,000 kL total volume. ' +
      'Charges are calculated on mass load (concentration × annual volume). ' +
      'For detailed charge calculations and Trade Waste Agreement conditions, refer to the SA Water Charges Estimate generated by the Reynolds & Bauhm Smart Water Assessment Tool.';
    const noteLines = doc.splitTextToSize(sawNote, cW - 8);
    const noteH = 8 + noteLines.length * 4;
    doc.setFillColor(255, 251, 235);
    doc.setDrawColor(217, 119, 6);
    doc.roundedRect(margin, y, cW, noteH, 2, 2, 'FD');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...C.amber);
    doc.text(noteLines, margin + 4, y + 5);
    y += noteH + 6;

    // ── Measured Parameters Summary ───────────────────────────────────────────
    y = sectionHead(doc, y, margin, pageW, 'Measured Parameter Summary — All Recorded Values');
  } else {
    // ── Discharge Compliance Table (non SA Water) ─────────────────────────────
    y = sectionHead(doc, y, margin, pageW, 'Discharge Compliance — Parameters vs. Regulatory Licence Limits');
  }

  // ASCII-safe parameter labels — WinAnsi does NOT support Unicode subscripts or ≤
  const PARAM_META: Record<string, { label: string; unit: string; limitKey?: keyof typeof std.limits }> = {
    tss:          { label: 'TSS',           unit: 'mg/L',    limitKey: 'tss' },
    bod:          { label: 'BOD5',          unit: 'mg/L',    limitKey: 'bod5' },
    bod5:         { label: 'BOD5',          unit: 'mg/L',    limitKey: 'bod5' },
    cod:          { label: 'COD',           unit: 'mg/L',    limitKey: 'cod' },
    og:           { label: 'O&G',           unit: 'mg/L',    limitKey: 'oilGrease' },
    oilgrease:    { label: 'O&G',           unit: 'mg/L',    limitKey: 'oilGrease' },
    ph:           { label: 'pH',            unit: 'pH units' },
    temperature:  { label: 'Temperature',   unit: '°C',      limitKey: 'temperature' },
    tds:          { label: 'TDS',           unit: 'mg/L',    limitKey: 'tds' },
    conductivity: { label: 'Conductivity',  unit: 'µS/cm',   limitKey: 'conductivity' },
    nh3n:         { label: 'NH3-N',         unit: 'mg/L',    limitKey: 'ammoniaN' },
    tp:           { label: 'Total P',       unit: 'mg/L',    limitKey: 'totalPhosphorus' },
    turbidity:    { label: 'Turbidity',     unit: 'NTU' },
    hardness:     { label: 'Hardness',      unit: 'mg/L CaCO3' },
    alkalinity:   { label: 'Alkalinity',    unit: 'mg/L CaCO3' },
    flowrate:     { label: 'Flow Rate',     unit: 'm3/day' },
    daf_hlr:      { label: 'DAF HLR',       unit: 'm3/m2/h' },
    daf_asr:      { label: 'DAF A/S Ratio', unit: '-' },
    // Metals — full limitKey mappings (previously showed N/A)
    lead:         { label: 'Lead (Pb)',      unit: 'mg/L',  limitKey: 'lead' },
    mercury:      { label: 'Mercury (Hg)',   unit: 'mg/L',  limitKey: 'mercury' },
    cadmium:      { label: 'Cadmium (Cd)',   unit: 'mg/L',  limitKey: 'cadmium' },
    chromium:     { label: 'Chromium (Cr)',  unit: 'mg/L',  limitKey: 'chromium' },
    arsenic:      { label: 'Arsenic (As)',   unit: 'mg/L',  limitKey: 'arsenic' },
    nickel:       { label: 'Nickel (Ni)',    unit: 'mg/L',  limitKey: 'nickel' },
    zinc:         { label: 'Zinc (Zn)',      unit: 'mg/L',  limitKey: 'zinc' },
    copper:       { label: 'Copper (Cu)',    unit: 'mg/L',  limitKey: 'copper' },
    // Other regulated parameters
    sulfide:      { label: 'Sulfides',       unit: 'mg/L',  limitKey: 'sulfides' },
    sulfides:     { label: 'Sulfides',       unit: 'mg/L',  limitKey: 'sulfides' },
    phenol:       { label: 'Phenol',         unit: 'mg/L',  limitKey: 'phenol' },
    surfactants:  { label: 'Surfactants',    unit: 'mg/L',  limitKey: 'surfactants' },
    chloride:     { label: 'Chlorides',      unit: 'mg/L',  limitKey: 'chlorides' },
    chlorides:    { label: 'Chlorides',      unit: 'mg/L',  limitKey: 'chlorides' },
    tn:           { label: 'Total N',        unit: 'mg/L',  limitKey: 'totalNitrogen' },
    totalnitrogen:{ label: 'Total N',        unit: 'mg/L',  limitKey: 'totalNitrogen' },
  };

  if (isSAWater) {
    // ── SA Water: simple measured parameters table (no pass/fail) ────────────
    const sawParamRows: string[][] = [];
    for (const [key, val] of Object.entries(params)) {
      if (val == null || isNaN(val)) continue;
      const meta = PARAM_META[key.toLowerCase()] || { label: key.toUpperCase(), unit: 'mg/L' };
      sawParamRows.push([
        meta.label,
        meta.unit,
        Number.isInteger(val) ? String(val) : val.toFixed(key === 'ph' ? 2 : 1),
        '—',  // no regulatory limit
      ]);
    }
    if (sawParamRows.length > 0) {
      autoTable(doc, {
        startY: y, margin: { left: margin, right: margin }, tableWidth: cW,
        head: [['Parameter', 'Unit', 'Measured Value', 'Notes']],
        body: sawParamRows,
        headStyles: { fillColor: [3, 105, 161], textColor: C.white, fontStyle: 'bold', fontSize: 8, cellPadding: 3 },
        styles: { fontSize: 8.5, cellPadding: 2.5, overflow: 'linebreak' as const },
        columnStyles: {
          0: { cellWidth: 32, fontStyle: 'bold' },
          1: { cellWidth: 30, textColor: C.grey },
          2: { cellWidth: 36, halign: 'right' as const, fontStyle: 'bold', textColor: C.navy },
          3: { cellWidth: 'auto' as const, textColor: C.grey },
        },
        alternateRowStyles: { fillColor: C.greyBg }, theme: 'grid',
      });
      y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;
    }
    // SA Water summary banner
    if (y > pageH - 30) {
      doc.addPage();
      doc.setFillColor(...C.white);
      doc.rect(0, 0, pageW, pageH, 'F');
      y = drawPageHeader(doc, pageW, margin, headerText, reportNum) + 4;
    }
    y = sectionHead(doc, y, margin, pageW, 'Trade Waste Summary');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(3, 105, 161);
    const sawSummText = `${sawParamRows.length} parameter${sawParamRows.length !== 1 ? 's' : ''} recorded — refer to SA Water Charges Estimate for excess concentration charge calculations`;
    const sawSummLines = doc.splitTextToSize(sawSummText, cW - 10);
    const sawSummH = 10 + sawSummLines.length * 5;
    doc.setFillColor(232, 247, 253);
    doc.setDrawColor(56, 189, 248);
    doc.roundedRect(margin, y, cW, sawSummH, 2, 2, 'FD');
    doc.text(sawSummLines, pageW / 2, y + 7, { align: 'center' });
    y += sawSummH + 4;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...C.grey);
    doc.text(`Sample Ref: ${refId}  |  Report Ref: ${reportNum}  |  Agreement: ${stdLabel}  |  Date: ${genDate}`, margin, y);
    y += 6;
  } else {
    // ── Standard regulatory compliance table ──────────────────────────────────
    const complianceRows: string[][] = [];
    let passCount = 0; let failCount = 0; let naCount = 0;

    for (const [key, val] of Object.entries(params)) {
      if (val == null || isNaN(val)) continue;
      const meta = PARAM_META[key.toLowerCase()] || { label: key.toUpperCase(), unit: 'mg/L' };
      let limit = '-';
      let status = 'N/A';

      if (key.toLowerCase() === 'ph') {
        limit = std ? `${std.limits.phMin} to ${std.limits.phMax}` : '-';
        if (std) {
          const pass = val >= std.limits.phMin && val <= std.limits.phMax;
          status = pass ? 'PASS' : 'FAIL';
          pass ? passCount++ : failCount++;
        } else { status = 'N/A'; naCount++; }
      } else if (meta.limitKey && std) {
        const lim = std.limits[meta.limitKey] as number;
        limit = `<= ${lim} ${meta.unit}`;
        const pass = val <= lim;
        status = pass ? 'PASS' : 'FAIL';
        pass ? passCount++ : failCount++;
      } else {
        status = 'N/A'; naCount++;
      }

      complianceRows.push([
        meta.label,
        Number.isInteger(val) ? String(val) : val.toFixed(key === 'ph' ? 2 : 1),
        meta.unit,
        limit,
        status,
      ]);
    }

    autoTable(doc, {
      startY: y, margin: { left: margin, right: margin }, tableWidth: cW,
      head: [['Parameter', 'Measured Value', 'Unit', 'Licence Limit', 'Compliance']],
      body: complianceRows,
      headStyles: { fillColor: C.navyMid, textColor: C.white, fontStyle: 'bold', fontSize: 8, cellPadding: 3 },
      styles: { fontSize: 8.5, cellPadding: 2.5, overflow: 'linebreak' as const },
      columnStyles: {
        0: { cellWidth: 30, fontStyle: 'bold' },
        1: { cellWidth: 26, halign: 'right' as const, fontStyle: 'bold', textColor: C.navy },
        2: { cellWidth: 26 },
        3: { cellWidth: 50, halign: 'center' as const },
        4: { cellWidth: 'auto', halign: 'center' as const, fontStyle: 'bold' },
      },
      alternateRowStyles: { fillColor: C.greyBg }, theme: 'grid',
      didParseCell: (d: any) => {
        if (d.column.index === 4) {
          if (d.cell.raw === 'PASS') d.cell.styles.textColor = C.emerald;
          else if (d.cell.raw === 'FAIL') { d.cell.styles.textColor = C.red; d.cell.styles.fillColor = [254, 242, 242]; }
          else d.cell.styles.textColor = C.grey;
        }
      },
    });
    y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

    // Overall compliance summary
    if (y > pageH - 50) {
      doc.addPage();
      doc.setFillColor(...C.white);
      doc.rect(0, 0, pageW, pageH, 'F');
      y = drawPageHeader(doc, pageW, margin, stdText, reportNum) + 4;
    }
    y = sectionHead(doc, y, margin, pageW, 'Compliance Summary');
    const allPass = failCount === 0 && passCount > 0;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...(allPass ? C.emerald : (failCount > 0 ? C.red : C.grey)));
    const summText = allPass
      ? `ALL ${passCount} DISCHARGE PARAMETERS COMPLIANT — Effluent meets ${stdLabel} licence conditions`
      : failCount > 0
      ? `${failCount} PARAMETER${failCount > 1 ? 'S' : ''} EXCEED LICENCE LIMITS — ${passCount} compliant, ${naCount} not assessed`
      : `No discharge limits assessed for this standard`;
    const summLines = doc.splitTextToSize(summText, cW - 10);
    const summH = 10 + summLines.length * 5;
    doc.setFillColor(...(allPass ? [236, 253, 245] : (failCount > 0 ? [254, 242, 242] : [248, 250, 252])) as [number,number,number]);
    doc.setDrawColor(...(allPass ? C.emerald : (failCount > 0 ? C.red : C.greyLt)));
    doc.roundedRect(margin, y, cW, summH, 2, 2, 'FD');
    doc.text(summLines, pageW / 2, y + 7, { align: 'center' });
    y += summH + 4;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...C.grey);
    doc.text(`Sample Ref: ${refId}  |  Report Ref: ${reportNum}  |  Standard: ${stdLabel}  |  Date: ${genDate}`, margin, y);
    y += 6;

    // Compliance notes from standard
    if (std?.complianceNotes?.length) {
      if (y > pageH - 40) {
        doc.addPage();
        doc.setFillColor(...C.white);
        doc.rect(0, 0, pageW, pageH, 'F');
        y = drawPageHeader(doc, pageW, margin, stdText, reportNum) + 8;
      }
      y = sectionHead(doc, y, margin, pageW, 'Regulatory Compliance Notes');
      std.complianceNotes.forEach((note, i) => {
        if (y > pageH - 25) {
          doc.addPage();
          doc.setFillColor(...C.white);
          doc.rect(0, 0, pageW, pageH, 'F');
          y = drawPageHeader(doc, pageW, margin, stdText, reportNum) + 8;
        }
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...C.black);
        const noteLines = doc.splitTextToSize(`${i + 1}. ${sanitizeForPDF(note)}`, cW - 6);
        doc.text(noteLines, margin + 3, y);
        y += noteLines.length * 4.2 + 2;
      });
      y += 4;
    }
  }

  // Notes field
  if (opts.notes) {
    if (y > pageH - 40) {
      doc.addPage();
      doc.setFillColor(...C.white);
      doc.rect(0, 0, pageW, pageH, 'F');
      y = drawPageHeader(doc, pageW, margin, stdText, reportNum) + 8;
    }
    y = sectionHead(doc, y, margin, pageW, 'Sample Notes');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...C.black);
    const noteLines = doc.splitTextToSize(opts.notes, cW - 6);
    doc.text(noteLines, margin + 3, y);
    y += noteLines.length * 4.5 + 4;
  }

  // ── Disclaimer / Notice ───────────────────────────────────────────────────
  if (y > pageH - 38) {
    doc.addPage();
    doc.setFillColor(...C.white);
    doc.rect(0, 0, pageW, pageH, 'F');
    y = drawPageHeader(doc, pageW, margin, isSAWater ? headerText : stdText, reportNum) + 4;
  }

  let discFull: string;
  let discTitle: string;
  if (isSAWater) {
    discTitle = 'IMPORTANT NOTICE — SA WATER TRADE WASTE AGREEMENT — PREPARED BY REYNOLDS & BAUHM LTD';
    // Build as separate lines so each section is clearly distinct in the PDF
    const discLines: string[] = [
      `AUTHORSHIP: This Trade Waste Discharge Report — including all data collection, sample analysis, parameter calculations, charge estimations ` +
        `and report generation — has been prepared solely by Reynolds & Bauhm Ltd. Reynolds & Bauhm Ltd is the assessing consultant for this ` +
        `engagement and is not affiliated with, and does not act on behalf of, SA Water.`,
      `REGULATORY AUTHORITY: SA Water is the Sewerage Authority and Trade Waste Regulator for South Australia under the Sewerage Act 1929 (SA). ` +
        `SA Water issues and administers Trade Waste Permits/Agreements, sets and enforces discharge limits, conducts compliance monitoring, ` +
        `and may issue infringement notices or penalties for non-compliance.`,
      `LIMITATIONS: This assessment is indicative only. Actual Trade Waste Agreement conditions, permit limits and excess concentration charges ` +
        `must be confirmed directly with SA Water. Measured values must be validated by an accredited NATA laboratory prior to submission.`,
      `Sample Reference: ${refId}  |  Agreement Reference: ${stdLabel}`,
    ];
    discFull = discLines.join('  ');
  } else {
    const certBody2 = std?.report.certificationMark
      ? `Laboratory results should be validated by a ${std.report.certificationMark}-accredited laboratory. `
      : 'Measured values should be validated by an accredited laboratory. ';
    const stdNote2 = std?.report.complianceStatement || 'Preliminary assessment only — not for regulatory submission without professional review.';
    discTitle = 'IMPORTANT — REGULATORY NOTICE';
    discFull = `This effluent compliance report has been prepared by Reynolds & Bauhm Ltd for assessment purposes. ${stdNote2} ` +
      certBody2 +
      `Regulatory decisions must be made by a licensed engineer in consultation with the relevant authority. ` +
      `Sample Reference: ${refId}`;
  }
  if (isSAWater) {
    // SA Water: multi-section disclaimer with clearly labeled paragraphs
    const sawSections = [
      {
        label: 'AUTHORSHIP:',
        text: `This Trade Waste Discharge Report — including all data collection, sample analysis, parameter calculations, charge estimations ` +
          `and report generation — has been prepared solely by Reynolds & Bauhm Ltd. Reynolds & Bauhm Ltd is the assessing consultant ` +
          `for this engagement and is not affiliated with, and does not act on behalf of, SA Water.`,
      },
      {
        label: 'REGULATORY AUTHORITY:',
        text: `SA Water is the Sewerage Authority and Trade Waste Regulator for South Australia under the Sewerage Act 1929 (SA). ` +
          `SA Water issues and administers Trade Waste Permits/Agreements, sets and enforces discharge limits, conducts compliance ` +
          `monitoring, and may issue infringement notices or penalties for non-compliance.`,
      },
      {
        label: 'LIMITATIONS:',
        text: `This assessment is indicative only. Actual Trade Waste Agreement conditions, permit limits and excess concentration ` +
          `charges must be confirmed directly with SA Water. Measured values must be validated by an accredited NATA ` +
          `laboratory prior to submission.  Sample Ref: ${refId}  |  Agreement Ref: ${stdLabel}`,
      },
    ];

    // Pre-compute total height
    let totalH = 8; // title row
    const sectionTextWidth = cW - 8;
    const parsedSections = sawSections.map(s => {
      const bodyLines = doc.splitTextToSize(s.text, sectionTextWidth - 22);
      return { ...s, bodyLines };
    });
    for (const s of parsedSections) {
      totalH += 5 + s.bodyLines.length * 3.6 + 3;
    }

    doc.setFillColor(255, 251, 235);
    doc.setDrawColor(205, 164, 46);
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, y, cW, totalH, 2, 2, 'FD');
    doc.setLineWidth(0.2);

    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(146, 64, 14);
    doc.text(discTitle, margin + 3, y + 5);

    let sy = y + 10;
    for (const s of parsedSections) {
      // Bold label
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.8);
      doc.setTextColor(120, 50, 10);
      doc.text(s.label, margin + 3, sy);
      // Normal body text indented after label
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.8);
      doc.setTextColor(92, 63, 10);
      doc.text(s.bodyLines, margin + 25, sy);
      sy += s.bodyLines.length * 3.6 + 5;
    }
  } else {
    const discLines2 = doc.splitTextToSize(discFull, cW - 6);
    const discH2 = 10 + discLines2.length * 3.8;
    doc.setFillColor(255, 251, 235);
    doc.setDrawColor(205, 164, 46);
    doc.roundedRect(margin, y, cW, discH2, 2, 2, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(146, 64, 14);
    doc.text(discTitle, margin + 3, y + 5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(92, 63, 10);
    doc.text(discLines2, margin + 3, y + 10);
  }

  // Watermarks + Footers
  const pc = doc.getNumberOfPages();
  stampAllPages(doc, pageW, pageH, opts.standard);
  const footerStdLabel = isSAWater ? 'SA Water Trade Waste Agreement' : stdLabel;
  const footerNote = isSAWater
    ? 'SA Water Trade Waste — indicative assessment only — confirm with SA Water'
    : (std?.report.complianceStatement || 'Preliminary assessment only');
  drawFooters(doc, pc, pageW, pageH, margin, reportNum, footerStdLabel, footerNote);

  if (mode === 'preview') return URL.createObjectURL(doc.output('blob'));
  const safeName = (opts.sampleName || reportNum).replace(/[^a-zA-Z0-9_\-]/g, '_').slice(0, 40);
  doc.save(`${reportNum}_EffluentComplianceReport_${safeName}.pdf`);
}

// ─── Combined Influent → DAF → Effluent Chain Report ─────────────────────────

export interface ChainReportOptions {
  chainReportNumber: string;
  influentReportNumber?: string;
  dafReportNumber?: string;
  effluentReportNumber?: string;
  actualEffluentReportNumber?: string;
  customerName?: string;
  siteName?: string;
  siteAddress?: string;
  permitRef?: string;
  regulatoryAuthority?: string;
  preparedBy?: string;
  standard?: string;
  createdAt?: Date | string;
  /** Actual measured effluent params for predicted vs actual delta analysis */
  actualEfluent?: Record<string, number> | null;
}

export interface ChainInfluentParams {
  ph?: number; cod?: number; bod?: number; tss?: number;
  nh3n?: number; tp?: number; oilgrease?: number; tds?: number;
  conductivity?: number; temperature?: number; turbidity?: number;
  flowrate?: number;
}

/**
 * Generates a single combined PDF showing the full treatment chain:
 * Influent Sample → DAF Design → Effluent Sample
 */
export function generateInfluentDafEffluentReportPDF(
  influentParams: ChainInfluentParams,
  dafParams: DAFReportParams,
  dafResult: DAFReportResult,
  effluentParams: Record<string, number>,
  opts: ChainReportOptions,
  mode: 'save' | 'preview' = 'save',
): string | undefined {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 18;
  const cW = pageW - margin * 2;

  doc.setFillColor(...C.white);
  doc.rect(0, 0, pageW, pageH, 'F');

  const std = opts.standard ? ALL_STANDARDS.find(s => s.code === opts.standard) || null : null;
  const stdText = std ? `${std.shortName} — ${std.country}` : (opts.standard || 'UK EA');
  const genDate = fmtDate(new Date());
  const chainRef = opts.chainReportNumber;

  // ── Page 1: Cover / summary ───────────────────────────────────────────────
  let y = drawPageHeader(doc, pageW, margin, stdText, chainRef) + 6;

  // Title block
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...C.navy);
  doc.text('INTEGRATED TREATMENT CHAIN REPORT', margin, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...C.grey);
  doc.text(opts.actualEfluent
    ? 'Influent Sample  ›  DAF Design  ›  Predicted Effluent  ›  Actual Effluent'
    : 'Influent Sample  ›  DAF Design  ›  Predicted Effluent (Modelled)',
    margin, y);
  y += 5;
  drawHr(doc, y, margin, pageW);
  y += 6;

  // Meta box
  const metaRows: [string, string][] = [
    ['Chain Report Ref', chainRef],
    ['Customer', opts.customerName || '—'],
    ['Site', opts.siteName || '—'],
    ...(opts.siteAddress ? [['Address', opts.siteAddress] as [string, string]] : []),
    ...(opts.permitRef   ? [['Permit Ref', opts.permitRef] as [string, string]] : []),
    ...(opts.regulatoryAuthority ? [['Regulatory Authority', opts.regulatoryAuthority] as [string, string]] : []),
    ['Standard', stdText],
    ['Prepared By', opts.preparedBy || 'Reynolds & Bauhm Ltd'],
    ['Date Generated', genDate],
    ...(opts.influentReportNumber ? [['Influent Report Ref', opts.influentReportNumber] as [string, string]] : []),
    ...(opts.dafReportNumber      ? [['DAF Report Ref',      opts.dafReportNumber]      as [string, string]] : []),
    ...(opts.effluentReportNumber ? [['Predicted Eff. Ref', opts.effluentReportNumber] as [string, string]] : []),
    ...(opts.actualEffluentReportNumber ? [['Actual Eff. Ref', opts.actualEffluentReportNumber] as [string, string]] : []),
  ];
  const colW = cW / 2 - 2;
  const rowH = 7;
  metaRows.forEach((row, i) => {
    const col = i % 2;
    const xOff = col === 0 ? margin : margin + colW + 4;
    if (i % 2 === 0) {
      const bg: [number,number,number] = Math.floor(i / 2) % 2 === 0 ? C.greyBg : C.white;
      doc.setFillColor(...bg);
      doc.rect(margin, y - 4, cW, rowH, 'F');
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...C.grey);
    doc.text(row[0] + ':', xOff, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.black);
    doc.text(row[1], xOff + 38, y);
    if (col === 1) y += rowH;
  });
  if (metaRows.length % 2 !== 0) y += rowH;
  y += 4;
  drawHr(doc, y, margin, pageW);
  y += 7;

  // ── Section 1: Influent Water Quality ────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...C.navy);
  doc.text('1  INFLUENT WATER QUALITY', margin, y);
  y += 5;

  const infRows: [string, string, string][] = [
    ['pH',              influentParams.ph            != null ? String(influentParams.ph.toFixed(2)) : '—', ''],
    ['COD',             influentParams.cod            != null ? String(influentParams.cod) : '—',          'mg/L'],
    ['BOD5',       influentParams.bod            != null ? String(influentParams.bod) : '—',          'mg/L'],
    ['TSS',             influentParams.tss            != null ? String(influentParams.tss) : '—',          'mg/L'],
    ['NH3-N',      influentParams.nh3n           != null ? String(influentParams.nh3n) : '—',         'mg/L'],
    ['Total P',         influentParams.tp             != null ? String(influentParams.tp) : '—',           'mg/L'],
    ['Oil & Grease',    influentParams.oilgrease      != null ? String(influentParams.oilgrease) : '—',    'mg/L'],
    ['Flow Rate',       influentParams.flowrate       != null ? String(influentParams.flowrate) : dafParams.flowRate != null ? String(dafParams.flowRate) : '—', 'm3/day'],
    ['Temperature',     influentParams.temperature    != null ? String(influentParams.temperature) : '—',  'deg C'],
    ['Conductivity',    influentParams.conductivity   != null ? String(influentParams.conductivity) : '—', 'uS/cm'],
    ['Turbidity',       influentParams.turbidity      != null ? String(influentParams.turbidity) : '—',    'NTU'],
  ].filter(r => r[1] !== '—');

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Parameter', 'Value', 'Unit']],
    body: infRows,
    styles: { fontSize: 8, cellPadding: 2.5, textColor: C.black, lineColor: C.greyLt, lineWidth: 0.2 },
    headStyles: { fillColor: C.navy, textColor: C.white, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: C.greyBg },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 45 }, 1: { halign: 'right', cellWidth: 30 }, 2: { cellWidth: 25 } },
    theme: 'grid',
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 8;

  // ── Section 2: DAF Design Summary ────────────────────────────────────────
  if (y > pageH - 60) {
    doc.addPage();
    doc.setFillColor(...C.white);
    doc.rect(0, 0, pageW, pageH, 'F');
    y = drawPageHeader(doc, pageW, margin, stdText, chainRef) + 8;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...C.navy);
  doc.text('2  DAF DESIGN SUMMARY', margin, y);
  y += 5;

  const dafRows: [string, string, string][] = [
    ['Flow Rate',            String(dafParams.flowRate),                         'm3/day'],
    ['Tank Length',          dafParams.length   != null ? String(dafParams.length) : '—',   'm'],
    ['Tank Width',           dafParams.width    != null ? String(dafParams.width) : '—',    'm'],
    ['Tank Depth',           dafParams.depth    != null ? String(dafParams.depth) : '—',    'm'],
    ['Air Pressure',         dafParams.airPressure != null ? String(dafParams.airPressure) : '—', 'bar'],
    ['Recycle Rate',         dafParams.recycleRate != null ? String(dafParams.recycleRate) : '—', '%'],
    ['HLR',                  dafResult.hlr.toFixed(3),                                           'm/h'],
    ['Air:Solids Ratio',     dafResult.airToSolids.toFixed(3),                                   ''],
    ['TSS Removal',          (dafResult.tssRemoval * 100).toFixed(1) + '%',                      ''],
    ['O&G Removal',          (dafResult.ogRemoval  * 100).toFixed(1) + '%',                      ''],
    ['BOD Removal',          (dafResult.bodRemoval * 100).toFixed(1) + '%',                      ''],
    ['COD Removal',          (dafResult.codRemoval * 100).toFixed(1) + '%',                      ''],
    ['Design Status',        dafResult.status,                                                    ''],
  ];

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Parameter', 'Value', 'Unit']],
    body: dafRows,
    styles: { fontSize: 8, cellPadding: 2.5, textColor: C.black, lineColor: C.greyLt, lineWidth: 0.2 },
    headStyles: { fillColor: [30, 58, 95] as [number,number,number], textColor: C.white, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: C.greyBg },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 45 }, 1: { halign: 'right', cellWidth: 30 }, 2: { cellWidth: 25 } },
    theme: 'grid',
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 8;

  // ── Section 3: Effluent Quality & Compliance ─────────────────────────────
  if (y > pageH - 60) {
    doc.addPage();
    doc.setFillColor(...C.white);
    doc.rect(0, 0, pageW, pageH, 'F');
    y = drawPageHeader(doc, pageW, margin, stdText, chainRef) + 8;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...C.navy);
  doc.text('3  EFFLUENT QUALITY & REGULATORY COMPLIANCE', margin, y);
  y += 5;

  const EFFLUENT_PARAM_LABELS: Record<string, string> = {
    ph: 'pH', cod: 'COD', bod: 'BOD', bod5: 'BOD5',
    tss: 'TSS', nh3n: 'NH3-N', tp: 'Total P',
    oilgrease: 'Oil & Grease', tds: 'TDS',
    conductivity: 'Conductivity', temperature: 'Temp', turbidity: 'Turbidity',
    flowrate: 'Flow Rate',
  };
  const EFFLUENT_PARAM_UNITS: Record<string, string> = {
    ph: '', cod: 'mg/L', bod: 'mg/L', bod5: 'mg/L',
    tss: 'mg/L', nh3n: 'mg/L', tp: 'mg/L',
    oilgrease: 'mg/L', tds: 'mg/L',
    conductivity: 'uS/cm', temperature: 'deg C', turbidity: 'NTU',
    flowrate: 'm3/day',
  };

  const stdLimits = std?.limits;
  const limMap: Record<string, number | undefined> = {
    tss: stdLimits?.tss, bod: stdLimits?.bod5, bod5: stdLimits?.bod5,
    cod: stdLimits?.cod, nh3n: stdLimits?.ammoniaN,
    oilgrease: stdLimits?.oilGrease, ph: undefined,
  };

  const effRows: [string, string, string, string, number[]][] = Object.entries(effluentParams)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => {
      const lim = limMap[k];
      const val = Number(v);
      const status = lim === undefined ? 'n/a' : val <= lim ? 'PASS' : 'FAIL';
      const colour: [number,number,number] = status === 'PASS' ? C.emerald : status === 'FAIL' ? C.red : C.grey;
      return [
        EFFLUENT_PARAM_LABELS[k] || k.toUpperCase(),
        val.toFixed(k === 'ph' ? 2 : 1),
        EFFLUENT_PARAM_UNITS[k] || '',
        lim !== undefined ? String(lim) : '—',
        colour,
      ] as [string, string, string, string, number[]];
    });

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Parameter', 'Value', 'Unit', 'Limit', 'Status']],
    body: effRows.map(r => [...r.slice(0, 4), r[3] === '—' ? 'N/A' : Number(r[1]) <= Number(r[3]) ? 'PASS' : 'FAIL']),
    styles: { fontSize: 8, cellPadding: 2.5, textColor: C.black, lineColor: C.greyLt, lineWidth: 0.2 },
    headStyles: { fillColor: [5, 120, 80] as [number,number,number], textColor: C.white, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: C.greyBg },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 45 },
      1: { halign: 'right', cellWidth: 25 },
      2: { cellWidth: 20 },
      3: { halign: 'right', cellWidth: 20 },
      4: { halign: 'center', cellWidth: 20 },
    },
    didParseCell(data) {
      if (data.column.index === 4 && data.section === 'body') {
        const v = String(data.cell.raw);
        if (v === 'PASS') { data.cell.styles.textColor = C.emerald; data.cell.styles.fontStyle = 'bold'; }
        else if (v === 'FAIL') { data.cell.styles.textColor = C.red; data.cell.styles.fontStyle = 'bold'; }
        else { data.cell.styles.textColor = C.grey; }
      }
    },
    theme: 'grid',
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 8;

  // ── Footer disclaimer ─────────────────────────────────────────────────────
  if (y > pageH - 25) {
    doc.addPage();
    doc.setFillColor(...C.white);
    doc.rect(0, 0, pageW, pageH, 'F');
    y = drawPageHeader(doc, pageW, margin, stdText, chainRef) + 8;
  }
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7);
  doc.setTextColor(...C.grey);
  const disclaimer = 'This integrated report is prepared by Reynolds & Bauhm Ltd for assessment purposes only. Results are based on modelled DAF performance and must be validated by a licensed engineer before regulatory submission. Actual effluent quality may differ from modelled predictions.';
  const disclaimerLines = doc.splitTextToSize(disclaimer, cW);
  doc.text(disclaimerLines, margin, y);
  y += disclaimerLines.length * 3.8 + 4;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...C.grey);
  doc.text(`Chain Ref: ${chainRef}  |  Generated: ${genDate}  |  Reynolds & Bauhm Ltd`, margin, y);

  // ── Section 4: Predicted vs Actual Delta (if actual effluent provided) ────
  if (opts.actualEfluent && Object.keys(opts.actualEfluent).length > 0) {
    if (y > pageH - 70) {
      doc.addPage();
      doc.setFillColor(...C.white);
      doc.rect(0, 0, pageW, pageH, 'F');
      y = drawPageHeader(doc, pageW, margin, stdText, chainRef) + 8;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...C.navy);
    doc.text('4  PREDICTED vs ACTUAL DISCHARGE MONITORING', margin, y);
    y += 3;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...C.grey);
    doc.text('Validation of DAF model accuracy against measured effluent — key indicator for regulatory confidence', margin, y);
    y += 6;

    const DELTA_LABELS: Record<string, string> = {
      tss: 'TSS', bod: 'BOD', bod5: 'BOD5', cod: 'COD',
      oilgrease: 'Oil & Grease', ph: 'pH', nh3n: 'NH3-N',
      tp: 'Total P', tds: 'TDS', temperature: 'Temp',
    };
    const DELTA_UNITS: Record<string, string> = {
      tss: 'mg/L', bod: 'mg/L', bod5: 'mg/L', cod: 'mg/L',
      oilgrease: 'mg/L', ph: '', nh3n: 'mg/L',
      tp: 'mg/L', tds: 'mg/L', temperature: 'deg C',
    };

    // predicted = effluentParams (DAF modelled), actual = opts.actualEfluent
    const deltaRows: string[][] = [];
    const paramKeys = Object.keys(opts.actualEfluent);
    for (const k of paramKeys) {
      const actual = opts.actualEfluent[k];
      const predicted = effluentParams[k];
      if (actual === undefined || actual === null) continue;
      const label = DELTA_LABELS[k] || k.toUpperCase();
      const unit = DELTA_UNITS[k] || '';
      const predStr = predicted !== undefined ? predicted.toFixed(k === 'ph' ? 2 : 1) : 'N/A';
      const actStr = actual.toFixed(k === 'ph' ? 2 : 1);
      let deltaStr = '—';
      let withinStr = '—';
      if (predicted !== undefined && predicted !== 0) {
        const deltaPct = ((actual - predicted) / predicted) * 100;
        deltaStr = `${deltaPct > 0 ? '+' : ''}${deltaPct.toFixed(1)}%`;
        withinStr = Math.abs(deltaPct) <= 20 ? 'YES' : 'NO';
      } else if (predicted === 0 && actual === 0) {
        withinStr = 'YES';
        deltaStr = '0%';
      }
      deltaRows.push([label, unit, predStr, actStr, deltaStr, withinStr]);
    }

    if (deltaRows.length > 0) {
      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Parameter', 'Unit', 'Predicted', 'Actual', 'Delta', 'Within ±20%?']],
        body: deltaRows,
        styles: { fontSize: 8, cellPadding: 2.5, textColor: C.black, lineColor: C.greyLt, lineWidth: 0.2 },
        headStyles: { fillColor: [80, 40, 120] as [number,number,number], textColor: C.white, fontStyle: 'bold', fontSize: 8 },
        alternateRowStyles: { fillColor: C.greyBg },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 32 },
          1: { cellWidth: 18 },
          2: { halign: 'right', cellWidth: 25 },
          3: { halign: 'right', cellWidth: 25 },
          4: { halign: 'right', cellWidth: 22 },
          5: { halign: 'center', cellWidth: 28 },
        },
        didParseCell(data) {
          if (data.column.index === 5 && data.section === 'body') {
            const v = String(data.cell.raw);
            if (v === 'YES') { data.cell.styles.textColor = C.emerald; data.cell.styles.fontStyle = 'bold'; }
            else if (v === 'NO') { data.cell.styles.textColor = C.red; data.cell.styles.fontStyle = 'bold'; }
          }
          if (data.column.index === 4 && data.section === 'body') {
            const v = String(data.cell.raw);
            if (v.startsWith('+') && v !== '+0.0%') data.cell.styles.textColor = C.amber;
            else if (v.startsWith('-')) data.cell.styles.textColor = C.emerald;
          }
        },
        theme: 'grid',
      });
      y = ((doc as any).lastAutoTable?.finalY ?? y) + 5;
    }

    // Model accuracy summary
    const totalDelta = deltaRows.filter(r => r[5] !== '—');
    const withinCount = totalDelta.filter(r => r[5] === 'YES').length;
    const accuracy = totalDelta.length > 0 ? Math.round((withinCount / totalDelta.length) * 100) : null;
    if (accuracy !== null) {
      const summColor: [number,number,number] = accuracy >= 80 ? C.emerald : accuracy >= 60 ? C.amber : C.red;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...summColor);
      doc.text(`Model Accuracy: ${accuracy}% of parameters within ±20% of predicted values`, margin, y);
      y += 5;
    }
    y += 3;
  }

  // ── Watermarks + Footers + Output ────────────────────────────────────────
  const chainPageCount = doc.getNumberOfPages();
  const chainStdLabel = std?.report.dischargeStandardLabel || stdText;
  const chainStdNote  = std?.report.complianceStatement || 'Early assessment - not for regulatory submission without professional review.';
  stampAllPages(doc, pageW, pageH, opts.standard);
  drawFooters(doc, chainPageCount, pageW, pageH, margin, chainRef, chainStdLabel, chainStdNote);

  const filename = `RB-Chain-${chainRef.replace(/\//g, '-')}.pdf`;
  if (mode === 'preview') {
    const blob = doc.output('blob');
    return URL.createObjectURL(blob);
  }
  doc.save(filename);
  return undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// SA WATER TRADE WASTE — EXCESS CONCENTRATION CHARGES ESTIMATE REPORT
// ─────────────────────────────────────────────────────────────────────────────

export interface SAWaterChargeRow {
  label: string;
  fullName: string;
  avg: number | null;
  loadKg: number | null;
  rate: number;
  charge: number | null;
  triggered: boolean;
  exceeds: boolean;
  highRateApplied: boolean;
}

export interface SAWaterReportOpts {
  customerCompany: string;
  siteName: string;
  siteAddress: string;
  sitePostcode?: string;
  permitRef?: string;
  sampleLabel: string;       // e.g. "Combined — 6 samples" or "Sample: 15 March 2026"
  annualKL: number;
  flowRateM3day?: number;
  operatingDays?: number;
  volumeMethod: 'direct' | 'flowrate';
  rows: SAWaterChargeRow[];
  totalCharge: number;
  referenceNumber: string;
  preparedBy?: string;
  mode?: 'download' | 'preview';
}

const fmtAUDpdf = (n: number) =>
  `$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNumPdf = (n: number, dp = 1) =>
  n.toFixed(dp).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export function generateSAWaterChargesReport(opts: SAWaterReportOpts): string | undefined {
  const {
    customerCompany, siteName, siteAddress, sitePostcode, permitRef,
    sampleLabel, annualKL, flowRateM3day, operatingDays, volumeMethod,
    rows, totalCharge, referenceNumber, preparedBy,
    mode = 'download',
  } = opts;

  const doc  = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  const colW = pageW - margin * 2;

  const today     = new Date();
  const issuedDate = today.toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' });
  const anyCharged = rows.some(r => r.charge !== null && r.charge > 0);

  // ── Helper: section box heading ──────────────────────────────────────────
  const sectionHead = (doc: jsPDF, y: number, title: string): number => {
    doc.setFillColor(...C.navyMid);
    doc.roundedRect(margin, y, colW, 7, 1, 1, 'F');
    doc.setFont('courier', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...C.gold);
    doc.text(title.toUpperCase(), margin + 3, y + 5);
    return y + 10;
  };

  // ── Helper: two-col label/value row ──────────────────────────────────────
  const infoRow = (doc: jsPDF, y: number, label: string, value: string, indent = 0): number => {
    doc.setFont('courier', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...C.grey);
    doc.text(label, margin + indent, y);
    doc.setFont('courier', 'normal');
    doc.setTextColor(...C.black);
    doc.text(value, margin + 52 + indent, y, { maxWidth: colW - 55 - indent });
    return y + 5.5;
  };

  // ════════════════════════════════════════════════
  // PAGE 1 HEADER
  // ════════════════════════════════════════════════
  let y = drawPageHeader(doc, pageW, margin, 'SA WATER — TRADE WASTE AGREEMENT', referenceNumber);

  // Report title block
  doc.setFillColor(240, 245, 255);
  doc.rect(margin, y, colW, 18, 'F');
  doc.setFont('courier', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...C.navy);
  doc.text('TRADE WASTE EXCESS CONCENTRATION', margin + 4, y + 7);
  doc.text('CHARGES ESTIMATE', margin + 4, y + 13.5);
  doc.setFont('courier', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...C.grey);
  doc.text('SA Water Fees & Charges Schedule — Indicative Assessment Only', pageW - margin - 2, y + 7, { align: 'right' });
  doc.text('Not for submission without professional review', pageW - margin - 2, y + 12.5, { align: 'right' });
  y += 22;

  // Reference / Date row
  doc.setFont('courier', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...C.navyMid);
  doc.text(`REFERENCE:  ${referenceNumber}`, margin, y + 5);
  doc.text(`ISSUED:  ${issuedDate}`, pageW - margin, y + 5, { align: 'right' });
  y += 11;
  drawHr(doc, y, margin, pageW);
  y += 4;

  // ── SECTION 1: SITE DETAILS ────────────────────────────────────────────
  y = sectionHead(doc, y, '1. Site & Customer Details');
  y = infoRow(doc, y, 'Customer / Operator:', customerCompany);
  y = infoRow(doc, y, 'Site Name:', siteName);
  y = infoRow(doc, y, 'Site Address:', `${siteAddress}${sitePostcode ? '  ' + sitePostcode : ''}`);
  if (permitRef) y = infoRow(doc, y, 'Permit / Licence Ref:', permitRef);
  if (preparedBy) y = infoRow(doc, y, 'Prepared By:', preparedBy);
  y += 3;

  // ── SECTION 2: SAMPLE BASIS ────────────────────────────────────────────
  y = sectionHead(doc, y, '2. Sample Basis');
  y = infoRow(doc, y, 'Sample Reference:', sampleLabel);
  y = infoRow(doc, y, 'Regulatory Standard:', 'SA Water RWAS (Receiving Waters Acceptance Standard)');
  y += 3;

  // ── SECTION 3: DISCHARGE VOLUME ──────────────────────────────────────
  y = sectionHead(doc, y, '3. Discharge Volume');
  y = infoRow(doc, y, 'Volume Method:', volumeMethod === 'flowrate' ? 'Flow Rate Calculation' : 'Directly Entered');
  if (volumeMethod === 'flowrate' && flowRateM3day !== undefined) {
    y = infoRow(doc, y, 'Flow Rate:', `${fmtNumPdf(flowRateM3day, 1)} m³/day`);
    y = infoRow(doc, y, 'Operating Days:', `${operatingDays ?? 250} days/year`);
    y = infoRow(doc, y, 'Calculated Annual Volume:', `${fmtNumPdf(annualKL, 0)} kL/year`);
  } else {
    y = infoRow(doc, y, 'Annual Discharge Volume:', `${fmtNumPdf(annualKL, 0)} kL/year`);
  }
  const volumeTriggered = annualKL >= 10000;
  doc.setFont('courier', 'bold');
  doc.setFontSize(8);
  if (volumeTriggered) {
    doc.setTextColor(180, 30, 30);
    doc.text('▲  Volume trigger EXCEEDED (≥10,000 kL/yr) — all parameters are chargeable', margin + 3, y + 4);
  } else {
    doc.setTextColor(5, 100, 70);
    doc.text('✓  Below 10,000 kL/yr volume trigger — load-based triggers apply per parameter', margin + 3, y + 4);
  }
  doc.setTextColor(...C.black);
  y += 9;

  // ── SECTION 4: EXCESS CONCENTRATION CHARGES TABLE ────────────────────
  y = sectionHead(doc, y, '4. Excess Concentration Charges');

  const chargeHead = [
    { content: 'Parameter', styles: { halign: 'left' as const } },
    { content: 'Full Name', styles: { halign: 'left' as const } },
    { content: 'Avg\n(mg/L)', styles: { halign: 'right' as const } },
    { content: 'Annual Load\n(kg/yr)', styles: { halign: 'right' as const } },
    { content: 'Rate\n($/kg)', styles: { halign: 'right' as const } },
    { content: 'Annual\nCharge', styles: { halign: 'right' as const } },
  ];

  const chargeBody = rows.map(r => {
    const noData = r.avg === null;
    const charged = r.charge !== null && r.charge > 0;
    let chargeText = '—';
    if (!noData && annualKL > 0) {
      if (!r.triggered) chargeText = 'Below trigger';
      else if (!r.exceeds) chargeText = '≤ 650 mg/L';
      else chargeText = fmtAUDpdf(r.charge!);
    }
    return [
      r.label,
      r.fullName,
      noData ? '—' : fmtNumPdf(r.avg!, r.avg! < 10 ? 2 : 1),
      !r.loadKg ? '—' : fmtNumPdf(r.loadKg, 0),
      noData ? '—' : `$${r.rate.toFixed(3)}`,
      chargeText,
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [chargeHead.map(h => h.content)],
    body: chargeBody,
    theme: 'grid',
    styles: { font: 'courier', fontSize: 8, cellPadding: 2.5 },
    headStyles: { fillColor: C.navy, textColor: C.white, fontStyle: 'bold', fontSize: 8 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 16 },
      1: { cellWidth: 55 },
      2: { halign: 'right', cellWidth: 20 },
      3: { halign: 'right', cellWidth: 22 },
      4: { halign: 'right', cellWidth: 18 },
      5: { halign: 'right', fontStyle: 'bold', cellWidth: 28 },
    },
    didParseCell: (data) => {
      if (data.section === 'body') {
        const chargeVal = data.row.raw[5] as string;
        if (chargeVal && chargeVal.startsWith('$') && parseFloat(chargeVal.replace(/[$,]/g, '')) > 0) {
          data.cell.styles.textColor = [180, 30, 30];
        }
        if (chargeVal === 'Below trigger') data.cell.styles.textColor = C.emerald;
      }
    },
    margin: { left: margin, right: margin },
  });

  y = ((doc as any).lastAutoTable?.finalY ?? y) + 4;

  // Totals box
  const totalExcl = totalCharge;
  const gst       = totalExcl * 0.1;
  const totalIncl = totalExcl + gst;

  doc.setFillColor(anyCharged ? 254 : 240, anyCharged ? 242 : 253, anyCharged ? 242 : 245);
  doc.setDrawColor(anyCharged ? 220 : 5, anyCharged ? 38 : 150, anyCharged ? 38 : 105);
  doc.setLineWidth(0.5);
  doc.roundedRect(margin, y, colW, 18, 1.5, 1.5, 'FD');

  doc.setFont('courier', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(anyCharged ? 180 : 5, anyCharged ? 30 : 100, anyCharged ? 30 : 70);
  doc.text('ESTIMATED ANNUAL EXCESS CHARGES (excl. GST)', margin + 4, y + 7);
  doc.setFontSize(13);
  doc.text(fmtAUDpdf(totalExcl), pageW - margin - 3, y + 8, { align: 'right' });
  doc.setFont('courier', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...C.grey);
  doc.text(`Including GST (10%):  ${fmtAUDpdf(totalIncl)}`, pageW - margin - 3, y + 14, { align: 'right' });
  doc.text(`Based on ${fmtNumPdf(annualKL, 0)} kL/yr  ·  SA Water Fees & Charges Schedule`, margin + 4, y + 14);
  y += 22;

  // ── SECTION 5: PERIOD BREAKDOWN ────────────────────────────────────────
  if (y > pageH - 80) { doc.addPage(); y = 20; }
  y = sectionHead(doc, y, '5. Period Charge Breakdown');

  const periodBody = [
    ...rows
      .filter(r => r.charge !== null && r.charge > 0)
      .map(r => [
        r.label,
        fmtAUDpdf(r.charge! / 12),
        fmtAUDpdf(r.charge! / 4),
        fmtAUDpdf(r.charge!),
      ]),
    [
      { content: 'TOTAL (excl. GST)', styles: { fontStyle: 'bold' as const } },
      { content: fmtAUDpdf(totalExcl / 12), styles: { fontStyle: 'bold' as const } },
      { content: fmtAUDpdf(totalExcl / 4),  styles: { fontStyle: 'bold' as const } },
      { content: fmtAUDpdf(totalExcl),       styles: { fontStyle: 'bold' as const } },
    ],
    [
      { content: 'TOTAL (incl. GST)', styles: { fontStyle: 'bold' as const, textColor: [180, 30, 30] as [number,number,number] } },
      { content: fmtAUDpdf(totalIncl / 12), styles: { fontStyle: 'bold' as const, textColor: [180,30,30] as [number,number,number] } },
      { content: fmtAUDpdf(totalIncl / 4),  styles: { fontStyle: 'bold' as const, textColor: [180,30,30] as [number,number,number] } },
      { content: fmtAUDpdf(totalIncl),       styles: { fontStyle: 'bold' as const, textColor: [180,30,30] as [number,number,number] } },
    ],
  ];

  autoTable(doc, {
    startY: y,
    head: [['Parameter', 'Monthly', 'Quarterly', 'Annual']],
    body: periodBody as any,
    theme: 'striped',
    styles: { font: 'courier', fontSize: 8.5, cellPadding: 3, halign: 'right' },
    headStyles: { fillColor: C.navyMid, textColor: C.white, fontStyle: 'bold', fontSize: 8.5 },
    columnStyles: {
      0: { halign: 'left', fontStyle: 'bold', cellWidth: 40 },
      1: { halign: 'right' },
      2: { halign: 'right' },
      3: { halign: 'right' },
    },
    margin: { left: margin, right: margin },
  });

  y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

  // ── SECTION 6: CHARGE TRIGGERS REFERENCE ─────────────────────────────
  if (y > pageH - 60) { doc.addPage(); y = 20; }
  y = sectionHead(doc, y, '6. Charge Trigger Reference (SA Water Schedule)');

  const triggerData = [
    ['BOD₅', '>10,000 kg/yr  OR  volume >10,000 kL/yr', '$0.251/kg (≤1,000 mg/L)  ·  $0.378/kg (>1,000 mg/L)'],
    ['SS',   '>10,000 kg/yr  OR  volume >10,000 kL/yr', '$0.218/kg (≤500 mg/L)  ·  $0.315/kg (>500 mg/L)'],
    ['TDS',  '>20,000 kg/yr  OR  volume >10,000 kL/yr', '$0.126/kg'],
    ['TKN',  'Concentration >650 mg/L  (all loads)',    '$0.392/kg'],
    ['TP',   'Any load once triggered',                  '$1.908/kg'],
  ];

  autoTable(doc, {
    startY: y,
    head: [['Parameter', 'Charge Trigger', 'Rate (excl. GST)']],
    body: triggerData,
    theme: 'grid',
    styles: { font: 'courier', fontSize: 7.5, cellPadding: 2 },
    headStyles: { fillColor: C.navy, textColor: C.gold, fontStyle: 'bold', fontSize: 7.5 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 14 },
      1: { cellWidth: 75 },
      2: { halign: 'left' },
    },
    margin: { left: margin, right: margin },
  });

  y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

  // ── DISCLAIMER ────────────────────────────────────────────────────────
  if (y > pageH - 50) { doc.addPage(); y = 20; }
  doc.setFillColor(255, 251, 235); // amber-50
  doc.setDrawColor(217, 119, 6);
  doc.setLineWidth(0.4);
  doc.roundedRect(margin, y, colW, 28, 1.5, 1.5, 'FD');

  doc.setFont('courier', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(146, 64, 14);
  doc.text('DISCLAIMER — INDICATIVE ESTIMATE ONLY', margin + 3, y + 6);
  doc.setFont('courier', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(92, 45, 10);
  const disclaimer = [
    'This estimate is based on sample averages and the discharge volume provided. Actual charges will be',
    'determined by SA Water under the Trade Waste Agreement. Mass loads, trigger thresholds and rates are',
    'sourced from the SA Water Fees & Charges Schedule and may change without notice.',
    'This report does not constitute a formal trade waste assessment. Contact SA Water for a formal agreement.',
  ];
  disclaimer.forEach((line, i) => {
    doc.text(line, margin + 3, y + 13 + i * 4.2);
  });
  y += 32;

  // ── SIGNATURE BLOCK ───────────────────────────────────────────────────
  if (y > pageH - 40) { doc.addPage(); y = 20; }
  drawHr(doc, y, margin, pageW);
  y += 5;

  doc.setFont('courier', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...C.grey);
  const signLeftX  = margin;
  const signMidX   = margin + colW / 2 + 5;
  doc.text('Prepared By:', signLeftX, y);
  doc.text('Authorised By:', signMidX, y);
  y += 5;
  doc.setDrawColor(...C.greyLt);
  doc.setLineWidth(0.4);
  doc.line(signLeftX, y, signLeftX + 70, y);
  doc.line(signMidX, y, signMidX + 70, y);
  y += 4;
  doc.setFont('courier', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...C.navyMid);
  if (preparedBy) doc.text(preparedBy, signLeftX, y);
  doc.text(issuedDate, signLeftX, y + 5);

  // ── WATERMARK + FOOTER ────────────────────────────────────────────────
  stampAllPages(doc, pageW, pageH, 'SA_WATER');
  const pageCount = doc.getNumberOfPages();
  drawFooters(
    doc, pageCount, pageW, pageH, margin,
    referenceNumber,
    'SA Water — Trade Waste Excess Concentration Charges',
    'Indicative estimate only — subject to SA Water Trade Waste Agreement. Reynolds & Bauhm Ltd.',
  );

  const filename = `RB-SAWater-Charges-${referenceNumber.replace(/\//g, '-')}.pdf`;
  if (mode === 'preview') {
    const blob = doc.output('blob');
    return URL.createObjectURL(blob);
  }
  doc.save(filename);
  return undefined;
}
